import React from "react";
import {
  AppBar, Toolbar, Typography, Container, Box, Grid, Card, CardContent, CardActions,
  IconButton, Button, Badge, Drawer, List, ListItem,
  Avatar, Tabs, Tab, TextField, InputAdornment, Snackbar, Alert,
  CircularProgress, Stack, Skeleton, CssBaseline, GlobalStyles
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { ShoppingCart, Add, Remove, Search, Send, Tune, Close, InfoOutlined } from "@mui/icons-material";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";

import { explainBackendError, validateOrderPayload, cents, fromc } from "./components/validationHelpers";
import PaymentSheet from "./components/PaymentSheet";
import ComboPickerSheet from "./components/ComboPickerSheet";
import OrderSummary from "./components/OrderSummary";
import ProductInfoModal from "./components/ProductInfoModal";
import BrandedImageFallback from "./components/BrandedImageFallback";
import ClientCheckoutAuthPanel from "./components/ClientCheckoutAuthPanel";
import Config from "./components/ClientConfig";
import OptimizedConfigImage from "./components/OptimizedConfigImage";
import { useDataStore } from "./dataStore";
import { applyEntityTranslation, withLanguageParam } from "./i18n";
import { brandThemeCssVars, DEFAULT_BRAND_THEME, normalizeBrandTheme } from "./themeTokens";
import { useClientPortal } from "./clientPortal/ClientPortalContext";
import { adaptAuthenticatedOrderPayload } from "./clientPortal/orderBenefitAdapter";
import { toSafePaymentRedirectUrl } from "./paymentUrl";
import { withAppBasePath } from "./app/runtimeConfig";

function apiUrl(path) {
  if (!path) return path;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const base = String(Config.BASE_URL || "").replace(/\/$/, "");
  const cleanPath = String(path).startsWith("/") ? path : `/${path}`;
  return `${base}${cleanPath}`;
}
function getParam(name) {
  return new URLSearchParams(window.location.search).get(name);
}
const tokenFromUrl = getParam("token") || null;

const DEFAULT_BRAND_NAME = "Restaurant";

const ENDPOINTS = {
  location: (loc) => `${Config.API_URL_LOCATIONS}${encodeURIComponent(loc)}`,
  locations: () => `${Config.API_URL_LOCATIONS}list_locations/`,
  priceList: (loc) => `${Config.BASE_API_URL}priceList/?location=${encodeURIComponent(loc)}`,
  promotionList: (loc) => `${Config.BASE_API_URL}promotionList/?location=${encodeURIComponent(loc)}`,
  products: (language) => withLanguageParam(`${Config.API_URL_PRODUCT}list_products/`, language),
  vats: () => `${Config.API_URL_PRODUCT}list_vats/`,
  combos: (language) => withLanguageParam(`${Config.BASE_API_URL}combo/list_combos/`, language),
  categories: (language) => withLanguageParam(`${Config.API_URL_PRODUCT_CATEGORY}`, language),

  startSkiplineSession: () => `${Config.API_URL_ORDERS}startSkiplineSession/`,
  startSkiplineSessionAlt: () => `${Config.BASE_API_URL}order/startSkiplineSession/`,

  createOrder: () => `${Config.API_URL_ORDERS}add/`,
  registerPay: () => `${Config.API_URL_ORDERS}registerPay/`,
  orderPayStatus: (id) => `${Config.API_URL_ORDERS}getPayStatus/?iref=${encodeURIComponent(id)}`,
  cancelOrder: (id) => `${Config.API_URL_ORDERS}cancelOrder/?iref=${encodeURIComponent(id)}`,
};

export const ORDER_REQUEST_CONTEXT = Object.freeze({
  guest: "guest",
  client: "client",
});

export function selectOrderRequestContext({
  authenticatedCheckoutEnabled,
  isAuthenticated,
}) {
  return authenticatedCheckoutEnabled && isAuthenticated
    ? ORDER_REQUEST_CONTEXT.client
    : ORDER_REQUEST_CONTEXT.guest;
}

export function extractCreatedOrderId(response, context) {
  if (context === ORDER_REQUEST_CONTEXT.client) {
    return response?.data?.order ?? null;
  }
  return (
    response?.data?.order ??
    response?.order ??
    response?.iref ??
    response?.data?.iref ??
    null
  );
}

export function extractServerOrderTotal(response) {
  const value = response?.data?.totalAmount;
  if (value == null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function extractRedirectPaymentUrl(response, context) {
  let raw;
  if (context === ORDER_REQUEST_CONTEXT.client) {
    raw = response?.data?.payUrl;
  } else {
    raw =
      response?.data?.payUrl ||
      response?.data?.url ||
      response?.data?.paymentUrl ||
      response?.payUrl ||
      response?.url ||
      response?.paymentUrl;
  }
  return toSafePaymentRedirectUrl(String(raw || ""));
}

let skiplineAccessToken = tokenFromUrl;

function getSafeStorage(name) {
  try {
    if (typeof window === "undefined") return null;

    const storage = window[name];
    const testKey = "__skipline_storage_test__";

    storage.setItem(testKey, "1");
    storage.removeItem(testKey);

    return storage;
  } catch (e) {
    return null;
  }
}

function safeStorageGet(name, key) {
  const storage = getSafeStorage(name);
  if (!storage) return null;

  try {
    return storage.getItem(key);
  } catch (e) {
    return null;
  }
}

function safeStorageSet(name, key, value) {
  const storage = getSafeStorage(name);
  if (!storage) return;

  try {
    storage.setItem(key, value);
  } catch (e) {
    // Storage can be blocked in embedded/private browser contexts.
  }
}

function safeStorageRemove(name, key) {
  const storage = getSafeStorage(name);
  if (!storage) return;

  try {
    storage.removeItem(key);
  } catch (e) {
    // Storage can be blocked in embedded/private browser contexts.
  }
}

function setSkiplineAccessToken(token) {
  skiplineAccessToken = token || null;

  if (token) {
    safeStorageSet("sessionStorage", "skiplineAccessToken", token);
  } else {
    safeStorageRemove("sessionStorage", "skiplineAccessToken");
  }
}

export function authHeaders() {
  const h = { "Content-Type": "application/json" };

  const token =
    skiplineAccessToken ||
    safeStorageGet("sessionStorage", "skiplineAccessToken") ||
    tokenFromUrl;

  if (token) {
    h.Authorization = `Bearer ${token}`;
  }

  return h;
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());
}

function money(n,currency="RON"){ if(typeof n!=="number"||Number.isNaN(n)) return "-"; return new Intl.NumberFormat("ro-RO",{style:"currency",currency,maximumFractionDigits:2}).format(n); }
function ensureSmallImage(path) {
  if (!path) return null;
  const base = Config.BASE_URL;
  const abs = path.startsWith("http") ? path : `${base}${path}`;
  const i = abs.lastIndexOf(".");
  return i === -1 ? abs : `${abs.slice(0, i)}_s${abs.slice(i)}`;
}
function arrify(raw,keys=[]){ if(!raw) return []; if(Array.isArray(raw)) return raw; for(let k of keys){ if(Array.isArray(raw[k])) return raw[k]; } if(Array.isArray(raw.results)) return raw.results; if(Array.isArray(raw.data)) return raw.data; if(Array.isArray(raw.items)) return raw.items; return []; }
function toCents(x){ return Math.round(Number(x)*100); }
function fromCents(c){ return Math.round(c)/100; }

const LEGACY_VAT_RATES_BY_ID = Object.freeze({
  "16ef0e4256": 21,
  "27554c6052": 0,
  "ad0aa21a47": 11,
});

function toVatRate(value) {
  if (value == null || value === "") return null;
  const rate = Number(value);
  return Number.isFinite(rate) && rate >= 0 && rate <= 100 ? rate : null;
}

export function buildVatRateMap(raw) {
  const rates = {};
  arrify(raw, ["VATS", "vats", "vat"]).forEach((item) => {
    const id = item?.iref ?? item?.id;
    const rate = toVatRate(item?.sell ?? item?.rate);
    if (id != null && rate != null) {
      rates[String(id)] = rate;
    }
  });
  return rates;
}

export function normalizeVatDefinition(
  rawVat,
  vatRatesById = {},
  explicitRate = null
) {
  const isObject = rawVat != null && typeof rawVat === "object";
  const rawId = isObject ? (rawVat.iref ?? rawVat.id) : rawVat;
  const vatId = rawId == null || rawId === "" ? null : String(rawId);
  const embeddedRate = isObject
    ? toVatRate(rawVat.sell ?? rawVat.rate)
    : null;
  const vatRate =
    embeddedRate ??
    toVatRate(explicitRate) ??
    (vatId != null ? toVatRate(vatRatesById?.[vatId]) : null) ??
    (vatId != null ? toVatRate(LEGACY_VAT_RATES_BY_ID[vatId]) : null);

  return { vatId, vatRate };
}

function buildEffectiveMaps(pl, promoRaw) {
  const promoObj = promoRaw?.promotionList || promoRaw || {};
  const promoByProductId = {};
  (arrify(promoObj, ["productPromotion_list"]) || []).forEach(l => {
    if (l?.isActive !== false && l?.product && typeof l?.value === "number") {
      promoByProductId[l.product] = Number(l.value);
    }
  });
  const promoByComboId = {};
  (arrify(promoObj, ["comboPromotion_list"]) || []).forEach(l => {
    if (l?.isActive !== false && l?.productCombo && typeof l?.value === "number") {
      promoByComboId[l.productCombo] = Number(l.value);
    }
  });

  const effectiveProd = { ...(pl?.priceByProductId || {}) };
  Object.keys(promoByProductId).forEach(pid => { effectiveProd[pid] = promoByProductId[pid]; });

  const effectiveCombo = { ...(pl?.priceByComboId || {}) };
  Object.keys(promoByComboId).forEach(cid => { effectiveCombo[cid] = promoByComboId[cid]; });

  return { promoByProductId, promoByComboId, effectiveProd, effectiveCombo };
}

function parseHMS(hms) { // "HH:MM:SS" -> minutes from midnight
  if (!hms) return null;
  const [H, M, S] = hms.split(":").map(Number);
  return (H * 60) + (M || 0) + Math.floor((S || 0) / 60);
}

function fmtHM(mins) { // 780 -> "13:00"
  if (mins == null) return "--:--";
  let h = Math.floor(mins / 60);
  let m = mins % 60;
  return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}`;
}

// Map JS getDay() (0..6, Sunday=0) to API day (1..7, Monday..Sunday)
function apiWeekdayFromDate(d = new Date()) {
  const js = d.getDay();        // 0..6
  return js === 0 ? 7 : js;     // 1..7
}

function evalLocationOpen(info, now = new Date(), t = (key, fallback) => fallback || key) {
  if (!info) return { openNow:false, reason:t("skipline.noLocationInfo") };

  // status & serveType
  const allowedServe = new Set([2,4,6,9]);
  if (info.status !== 1) return { openNow:false, reason:t("skipline.inactiveLocation") };
  if (!allowedServe.has(info.serveType)) return { openNow:false, reason:t("skipline.unsupportedServeType") };

  const day = apiWeekdayFromDate(now);
  const schedules = Array.isArray(info.locationSchedule_location) ? info.locationSchedule_location : [];
  const todayIntervals = schedules.filter(x => x.day === day);

  if (!todayIntervals.length) return { openNow:false, reason:t("skipline.noTodaySchedule") };

  const openDelta = Number(info.orderOpenDeltaMin || 0);
  const closeDelta = Number(info.orderCloseDeltaMin || 0);
  const nowMins = now.getHours() * 60 + now.getMinutes();

  // Check any interval open
  let openNow = false;
  let todayOpen = null;
  let todayClose = null;
  for (const it of todayIntervals) {
    const start = parseHMS(it.interval_start);
    const end   = parseHMS(it.interval_end);
    if (start == null || end == null) continue;

    const adjStart = Math.max(0, start + openDelta);
    const adjEnd   = Math.max(adjStart, end + closeDelta); // closeDelta is less than 0

    // store the window (first interval for display)
    if (todayOpen == null || adjStart < todayOpen) todayOpen = adjStart;
    if (todayClose == null || adjEnd   > todayClose) todayClose = adjEnd;

    if (nowMins >= adjStart && nowMins < adjEnd) {
      openNow = true;
    }
  }

  const reason = openNow
    ? ""
    : t("skipline.closedSchedule")
      .replace("{open}", fmtHM(todayOpen))
      .replace("{close}", fmtHM(todayClose));

  return { openNow, todayOpen, todayClose, reason };
}

const NET_TIMEOUT_MS = 12000;
const withTimeout = (p, ms=NET_TIMEOUT_MS) =>
  Promise.race([p, new Promise((_,rej)=>setTimeout(()=>rej(new Error("Timeout")), ms))]);

class ApiClient {
  async get(url) {
    const fullUrl = apiUrl(url);

    try {
      const r = await withTimeout(fetch(fullUrl, { headers: authHeaders() }));
      const text = await r.text();

      if (!r.ok) throw new Error(`${r.status}: ${text.slice(0, 200)}`);

      try {
        return JSON.parse(text);
      } catch {
        throw new Error(`Invalid JSON from ${fullUrl}: ${text.slice(0, 200)}`);
      }
    } catch {
      return null;
    }
  }

  async post(url, body) {
    const fullUrl = apiUrl(url);

    const r = await fetch(fullUrl, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(body),
    });

    const text = await r.text();

    if (!r.ok) throw new Error(text || `${r.status}`);

    try {
      return JSON.parse(text);
    } catch {
      throw new Error(`Invalid JSON from ${fullUrl}: ${text.slice(0, 200)}`);
    }
  }

  async postPublic(url, body = {}) {
    const fullUrl = apiUrl(url);

    const r = await fetch(fullUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    const text = await r.text();

    if (!r.ok) throw new Error(text || `${r.status}`);

    try {
      return JSON.parse(text);
    } catch {
      throw new Error(`Invalid JSON from ${fullUrl}: ${text.slice(0, 200)}`);
    }
  }
}

const PAGE_BG = "var(--brand-bg, #fffaf3)";
const SURFACE = "var(--brand-surface, rgba(255,255,255,0.94))";
const BORDER = "rgba(124,45,18,0.10)";
const BRAND = "var(--brand-text, #431407)";
const ACCENT = "var(--brand-primary, #F7B500)";
const CTA = "var(--brand-cta, #0f766e)";

const focusRingSx = {
  "&:focus-visible": {
    outline: `3px solid ${CTA}`,
    outlineOffset: 3,
  },
};

const standaloneGlobalStyles = {
  "html, body": {
    touchAction: "manipulation",
    backgroundColor: PAGE_BG,
  },
  "button, [role='button'], .MuiButtonBase-root": {
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
  },
  "button:focus-visible, [role='button']:focus-visible, a:focus-visible, [tabindex='0']:focus-visible": {
    outline: `3px solid ${CTA}`,
    outlineOffset: 3,
  },
  img: {
    maxWidth: "100%",
  },
  "@media (prefers-reduced-motion: reduce)": {
    "html:focus-within": {
      scrollBehavior: "auto",
    },
    "*, *::before, *::after": {
      animationDuration: "0.01ms !important",
      animationIterationCount: "1 !important",
      scrollBehavior: "auto !important",
      transitionDuration: "0.01ms !important",
    },
  },
};

function createSkiplineTheme(brandTheme = DEFAULT_BRAND_THEME) {
  const c = normalizeBrandTheme(brandTheme);

  return createTheme({
    palette: {
      primary: { main: c.primary, contrastText: c.onPrimary },
      secondary: { main: c.cta, contrastText: c.onCta },
      background: { default: c.background, paper: c.surface },
      text: { primary: c.text, secondary: c.muted },
    },
    shape: { borderRadius: 14 },
    typography: {
      fontFamily:
        "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, \"Segoe UI\", sans-serif",
      button: { textTransform: "none" },
    },
    components: {
      MuiButtonBase: {
        styleOverrides: {
          root: {
            "&.Mui-focusVisible": {
              outline: `3px solid ${c.cta}`,
              outlineOffset: 3,
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            minHeight: 44,
            borderRadius: 12,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            minWidth: 44,
            minHeight: 44,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 44,
          },
        },
      },
    },
  });
}

const appBarSx = {
  bgcolor: PAGE_BG,
  color: BRAND,
  borderBottom: "1px solid",
  borderColor: "rgba(67,20,7,0.10)",
  backdropFilter: "blur(18px)",
  boxShadow: "0 10px 34px rgba(33,20,12,0.08)",
};

const brandMarkSx = {
  width: 42,
  height: 42,
  position: "relative",
  borderRadius: "12px",
  bgcolor:
    "color-mix(in srgb, var(--brand-primary, #f7b500) 18%, var(--brand-surface, #fff))",
  border: "1px solid rgba(124,45,18,0.10)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  flexShrink: 0,
};

const shellCardSx = {
  borderRadius: "24px",
  border: "1px solid",
  borderColor: BORDER,
  boxShadow: "0 20px 48px rgba(33,20,12,0.10)",
  overflow: "hidden",
  bgcolor: SURFACE,
};

const compactCardSx = {
  borderRadius: "16px",
  border: "1px solid",
  borderColor: BORDER,
  boxShadow: "0 14px 34px rgba(33,20,12,0.08)",
  overflow: "hidden",
  bgcolor: SURFACE,
};

const pillButtonSx = {
  borderRadius: "12px",
  fontWeight: 900,
  textTransform: "none",
  ...focusRingSx,
};

export class SkipTheLinePage extends React.Component {
  constructor(props){
    super(props);
    this.state = {
      location: safeStorageGet("localStorage", "selectedSkiplineLocation") || null,
      locationList: [],
      selectingLocation: false,
      expandedCounty: null,
      locationSearch: "",
      locationInfo: null,
      locationOpenNow: false,
      locationReason: "",   // explanation if blocked
      loading: true, error: "", priceList:null,
      promoList: null,                        // raw promo list
      promoByProductId: {},                   // productId -> promo price (RON)
      promoByComboId: {},                     // comboId   -> promo price (RON) if present
      effectiveProd: {},                      // productId -> price used (RON)
      effectiveCombo: {},                     // comboId   -> price used (RON)
      vatRatesById: {},                       // VAT iref -> sell percentage
      priceByProductId: {},   // base price list
      priceByComboId:   {},   // base price list
      productsAll:[], products:[], combos:[],
      categories:[], categoriesRaw:[], subcategories:[],
      view:"name", activeCategory:"All", search:"",
      cartOpen:false, cartProducts:{}, cartCombos:{},
      pickerOpen:false, pickerCombo:null,
      infoProduct:null,
      customerName:"",
      guestEmail:"",
      placing:false, orderLocked:false, lastOrderId:null, lastOrderGross:null,
      lastOrderContext:null,
      paymentSheetOpen:false, paymentChecking:false, paymentMessage:"", payUrl:"", payIframeError:false,
      paymentRegistering:false, paymentRegistrationFailed:false,
      clientSessionRequired:false,
      paymentProvider: "PayU",
      paymentProviderConfirmed: false,
      summaryOpen:false,
    };
    this.api = props.api || new ApiClient();
    this.pollTimer = null;
    this.orderAddInFlight = false;
    this.registerPayPromise = null;
    this.pendingCreatedOrder = null;
  }

  getBrandName = () => {
    const configuredName = String(this.props.brandName || "").trim();
    if (configuredName) return configuredName;
    return DEFAULT_BRAND_NAME;
  };

  t = (key, fallback = "") => {
    return this.props.t ? this.props.t(key, fallback) : fallback || key;
  };

  tr = (key, replacements = {}, fallback = "") => {
    let text = this.t(key, fallback);
    Object.keys(replacements).forEach((name) => {
      text = text.replaceAll(`{${name}}`, replacements[name]);
    });
    return text;
  };

  getBrandNameUpper = () => this.getBrandName().toLocaleUpperCase("ro-RO");

  getBrandInitial = () => {
    const brandName = this.getBrandName();
    return brandName ? brandName.charAt(0).toLocaleUpperCase("ro-RO") : "";
  };

  getCategoryIdForName = (name, categoriesRaw = this.state.categoriesRaw) => {
    if (!name || name === "All" || name === "Combos") return null;

    const category = (categoriesRaw || []).find(
      (item) => String(item?.name || "") === String(name)
    );

    return category?.iref || category?.id || null;
  };

  getCategoryNameForId = (id, categoriesRaw = this.state.categoriesRaw) => {
    if (!id) return null;

    const category = (categoriesRaw || []).find(
      (item) => String(item?.iref || item?.id || "") === String(id)
    );

    return category?.name || null;
  };

  resolveActiveCategoryAfterReload = (activeCategory, previousCategoriesRaw, nextCategoriesRaw) => {
    if (activeCategory === "All" || activeCategory === "Combos") return activeCategory;

    const previousCategoryId = this.getCategoryIdForName(activeCategory, previousCategoriesRaw);
    const nextCategoryName = this.getCategoryNameForId(previousCategoryId, nextCategoriesRaw);

    if (nextCategoryName) return nextCategoryName;

    const stillAvailable = (nextCategoriesRaw || []).some(
      (category) => String(category?.name || "") === String(activeCategory)
    );

    return stillAvailable ? activeCategory : "All";
  };

  renderLanguageSwitcher = (sx = {}) => {
    const languages = this.props.languages || [];
    if (languages.length < 2) return null;

    const selectedLanguage =
      languages.find((language) => String(language.iref) === String(this.props.selectedLanguageIref)) ||
      languages[0];
    const nextLanguage =
      languages.find((language) => String(language.iref) !== String(selectedLanguage?.iref)) ||
      selectedLanguage;
    const selectedCode = String(
      selectedLanguage?.countryShortName ||
      this.props.languageCode ||
      ""
    ).toUpperCase();

    const handleMobileToggle = () => {
      if (!nextLanguage?.iref || String(nextLanguage.iref) === String(selectedLanguage?.iref)) return;
      this.props.setSelectedLanguageIref?.(nextLanguage.iref);
    };

    return (
      <>
        <Button
          size="small"
          onClick={handleMobileToggle}
          aria-label={this.t("language.change")}
          sx={{
            display: { xs: "inline-flex", sm: "none" },
            minWidth: 44,
            minHeight: 44,
            px: 1,
            borderRadius: "12px",
            color: "secondary.contrastText",
            bgcolor: "secondary.main",
            border: "1px solid",
            borderColor: "secondary.main",
            flexShrink: 0,
            fontSize: 12,
            fontWeight: 950,
            lineHeight: 1,
            "&:hover": {
              bgcolor: "secondary.dark",
              borderColor: "secondary.dark",
            },
            ...sx,
          }}
        >
          {selectedCode}
        </Button>

        <Stack
          role="group"
          direction="row"
          spacing={0.25}
          aria-label={this.t("language.change")}
          sx={{
            display: { xs: "none", sm: "flex" },
            p: 0.25,
            borderRadius: "8px",
            border: "1px solid",
            borderColor: BORDER,
            bgcolor: "rgba(255,255,255,0.72)",
            flexShrink: 0,
            ...sx,
          }}
        >
          {languages.map((language) => {
            const code = String(language.countryShortName || "").toUpperCase();
            const active = String(language.iref) === String(this.props.selectedLanguageIref);

            return (
              <Button
                key={language.iref}
                size="small"
                onClick={() => this.props.setSelectedLanguageIref?.(language.iref)}
                aria-pressed={active}
                aria-label={`${this.t("language.change")} ${code}`}
                sx={{
                  minWidth: 44,
                  minHeight: 40,
                  px: 0.65,
                  borderRadius: "10px",
                  color: active ? "secondary.contrastText" : BRAND,
                  bgcolor: active ? "secondary.main" : "transparent",
                  fontSize: 12,
                  fontWeight: 950,
                  lineHeight: 1,
                  "&:hover": {
                    bgcolor: active ? "secondary.main" : "rgba(15,118,110,0.07)",
                  },
                }}
              >
                {code}
              </Button>
            );
          })}
        </Stack>
      </>
    );
  };

  renderBrandMark = (sx = {}) => {
    const { logoUrl } = this.props;
    const fallbackInitial = this.getBrandInitial();

    return (
      <Box sx={{ ...brandMarkSx, ...sx }}>
        <Typography
          aria-hidden="true"
          sx={{
            fontWeight: 900,
            color: "var(--brand-accent, #9a3412)",
            fontSize: 18,
            visibility: fallbackInitial ? "visible" : "hidden",
          }}
        >
          {fallbackInitial || "R"}
        </Typography>
        {logoUrl ? (
          <Box
            component={OptimizedConfigImage}
            src={logoUrl}
            alt={this.getBrandName()}
            decoding="async"
            onError={(event) => {
              event.currentTarget.style.display = "none";
            }}
            sx={{
              position: "absolute",
              inset: 0,
              m: "auto",
              maxHeight: 39,
              maxWidth: 39,
              width: "auto",
              display: "block",
            }}
          />
        ) : null}
      </Box>
    );
  };

  activateCardFromKeyboard = (event, action) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    action();
  };

  ensurePublicSession = async (loc = this.state.location) => {
    const existing =
      skiplineAccessToken ||
      safeStorageGet("sessionStorage", "skiplineAccessToken") ||
      tokenFromUrl;

    if (existing) {
      setSkiplineAccessToken(existing);
      return existing;
    }

    const body = {
      location: loc,
      source: "skipline_public",
    };

    let res = null;

    try {
      res = await this.api.postPublic(ENDPOINTS.startSkiplineSession(), body);
    } catch (firstErr) {
      res = await this.api.postPublic(ENDPOINTS.startSkiplineSessionAlt(), body);
    }

    const token =
      res?.access ||
      res?.data?.access ||
      res?.token ||
      res?.data?.token;

    if (!token) {
      throw new Error(this.t("skipline.loadingInit"));
    }

    setSkiplineAccessToken(token);
    return token;
  };

  loadPaymentProviderConfig = async () => {
    try {
      const cfg = await this.api.get(
        `${Config.API_URL_CONFIG_FRONT}?key=ORDER_PAYMENT_PROVIDER`
      );

      const rawProvider =
        cfg?.platform?.find((x) => x?.key === "ORDER_PAYMENT_PROVIDER")?.value ||
        cfg?.platform?.[0]?.value;
      if (!rawProvider) throw new Error("Payment provider is unavailable");

      const provider = String(rawProvider).toLowerCase().trim();
      if (!["payu", "bt", "stripe"].includes(provider)) {
        throw new Error("Payment provider is unsupported");
      }

      const paymentProvider =
        provider === "stripe"
          ? "Stripe"
          : provider === "bt"
            ? "BT"
            : "PayU";

      this.setState({ paymentProvider, paymentProviderConfirmed: true });
    } catch (e) {
      this.setState({
        paymentProvider: "PayU",
        paymentProviderConfirmed: false,
      });
    }
  };

  async componentDidMount() {
    try {
      this.setState({ selectingLocation: true });

      const resp = await this.api.get(ENDPOINTS.locations()) || {};
      const locationList = Array.isArray(resp.locations) ? resp.locations : [];

      locationList.sort((a, b) => (a.name || "").localeCompare(b.name || "", "ro"));

      const countyNames = [
        ...new Set(locationList.map((loc) => this.getCountyName(loc))),
      ];

      this.setState(
        {
          locationList,
          selectingLocation: false,
          loading: false,
          expandedCounty: countyNames.length === 1 ? countyNames[0] : null,
        },
        async () => {
          if (this.state.location) {
            try {
              this.setState({ loading: true });
              safeStorageRemove("sessionStorage", "skiplineAccessToken");
              setSkiplineAccessToken(null);

              await this.ensurePublicSession(this.state.location);
              await this.loadPaymentProviderConfig();
              await this.bootstrap(this.state.location);
            } catch {
              this.setState({
                loading: false,
                error: this.t("skipline.loadingMenu"),
              });
            }
          }
        }
      );
    } catch {
      this.setState({
        loading: false,
        selectingLocation: false,
        error: this.t("skipline.loadingInit"),
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selectedLanguageIref !== this.props.selectedLanguageIref && this.state.location) {
      this.bootstrap(this.state.location);
    }
  }

  componentWillUnmount(){ this.stopPolling(); }

  async bootstrap(loc = this.state.location) {
    try{
      const location = loc;
      const languageIref = this.props.selectedLanguageIref || "";
      const previousCategoriesRaw = this.state.categoriesRaw;
      const [locRaw, plRaw, promoRaw, prodsRaw, combosRaw, catsRaw, vatsRaw] = await Promise.all([
        this.api.get(ENDPOINTS.location(location)),
        this.api.get(ENDPOINTS.priceList(location)),
        this.api.get(ENDPOINTS.promotionList(location)).catch(()=>null),
        this.api.get(ENDPOINTS.products(languageIref)),
        this.api.get(ENDPOINTS.combos(languageIref)),
        this.api.get(ENDPOINTS.categories(languageIref)),
        this.api.get(ENDPOINTS.vats()).catch(()=>null),
      ]);

      const locInfo = locRaw || null;
      const openEval = evalLocationOpen(locInfo, new Date(), this.t);
      const pl = this.normalizePriceList(plRaw);
      const promoList = promoRaw?.promotionList || promoRaw || null;
      const vatRatesById = buildVatRateMap(vatsRaw);

      const { promoByProductId, promoByComboId, effectiveProd, effectiveCombo } =
        buildEffectiveMaps(pl, promoRaw);

      const catArr = arrify(catsRaw,["productCategories"]).map((category) =>
        applyEntityTranslation(category, "pct_product", this.props.selectedLanguage, this.props.defaultLanguage)
      );
      const subArr = [];
      const catMap = new Map(catArr.map(c=>[c.iref||c.id,c]));

      const productsAll = this.normalizeProducts(
        arrify(prodsRaw,["products","data","items"]).map((product) =>
          applyEntityTranslation(product, "pt_product", this.props.selectedLanguage, this.props.defaultLanguage)
        ),
        pl,
        catMap,
        effectiveProd,
        promoByProductId,
        vatRatesById
      );
      const products = productsAll.filter(p=>p.showStandalone);

      const combos = this.normalizeCombos(
        arrify(combosRaw, ["productCombos", "combos", "data"]).map((combo) =>
          applyEntityTranslation(combo, "pt_product", this.props.selectedLanguage, this.props.defaultLanguage)
        ),
        pl,
        effectiveCombo,
        promoByProductId,
        promoByComboId,
        catMap
      );

      const apiCats = catArr.filter(c=>c.isForSale!==false)
        .map(c=>({ id:c.iref||c.id, name:c.name })).filter(c=>!!c.name);
      const categories = ["All", ...apiCats.map(c=>c.name), "Combos"];

      this.setState((state) => ({
        priceList: pl,
        promoList,
        promoByProductId, promoByComboId,
        effectiveProd, effectiveCombo,
        vatRatesById,
        productsAll, products, combos, categories,
        categoriesRaw: catArr, subcategories: subArr,
        activeCategory: this.resolveActiveCategoryAfterReload(
          state.activeCategory,
          previousCategoriesRaw,
          catArr
        ),
        locationInfo: locInfo,
        locationOpenNow: openEval.openNow,
        locationReason: openEval.reason,
        loading: false,
      }));
    }catch(e){
      this.setState({ error:String(e.message||e), loading:false });
    }
  }

  selectLocation = async (locIref) => {
    if (!locIref) return;

    safeStorageSet("localStorage", "selectedSkiplineLocation", locIref);

    // Important: clear old public token before creating one for selected location
    safeStorageRemove("sessionStorage", "skiplineAccessToken");
    setSkiplineAccessToken(null);

    this.setState({
      location: locIref,
      loading: true,
      error: "",
      cartProducts: {},
      cartCombos: {},
      infoProduct: null,
      view: "name",
      activeCategory: "All",
      search: "",
    });

    try {
      await this.ensurePublicSession(locIref);
      await this.loadPaymentProviderConfig();
      await this.bootstrap(locIref);
    } catch {
      this.setState({
        loading: false,
        error: this.t("skipline.loadingSelectedLocation"),
      });
    }
  };

  changeLocation = () => {
    const hasItems =
      Object.keys(this.state.cartProducts || {}).length > 0 ||
      Object.keys(this.state.cartCombos || {}).length > 0;

    if (hasItems) {
      const confirmed = window.confirm(
        this.t("skipline.locationChangeConfirm")
      );
      if (!confirmed) return;
    }

    safeStorageRemove("localStorage", "selectedSkiplineLocation");
    safeStorageRemove("sessionStorage", "skiplineAccessToken");
    setSkiplineAccessToken(null);

    this.setState({
      location: null,
      locationInfo: null,
      priceList: null,
      promoList: null,
      vatRatesById: {},
      productsAll: [],
      products: [],
      combos: [],
      cartProducts: {},
      cartCombos: {},
      infoProduct: null,
      view: "name",
      activeCategory: "All",
      search: "",
      orderLocked: false,
      lastOrderId: null,
      lastOrderGross: null,
      lastOrderContext: null,
      payUrl: "",
      paymentProviderConfirmed: false,
      paymentRegistrationFailed: false,
      paymentRegistering: false,
      clientSessionRequired: false,
    });
    this.pendingCreatedOrder = null;
  };

  formatAddress = (address) => {
    if (!address) return "";
    if (typeof address === "string") return address;
    return [
      address.street,
      address.number,
      address.local,
    ].filter(Boolean).join(", ");
  };

  getCityName = (loc) => {
    const city = loc?.address?.city;
    if (!city) return this.t("skipline.otherCity");
    if (typeof city === "string") return city;
    return city.name || city.iref || this.t("skipline.otherCity");
  };

  getCountyName = (loc) => {
    return loc?.countyName || "Alt județ";
  };

  groupLocationsByCounty = () => {
    const q = String(this.state.locationSearch || "").trim().toLowerCase();
    const groups = {};
    (this.state.locationList || []).forEach((loc) => {
      const countyName = this.getCountyName(loc);
      const cityName = this.getCityName ? this.getCityName(loc) : loc?.address?.city;
      const addressText = this.formatAddress(loc.address);
      const haystack = [
        countyName,
        cityName,
        loc.name,
        addressText,
      ].filter(Boolean).join(" ").toLowerCase();
      if (q && !haystack.includes(q)) {
        return;
      }
      if (!groups[countyName]) {
        groups[countyName] = [];
      }
      groups[countyName].push(loc);
    });

    return Object.keys(groups)
      .sort((a, b) => a.localeCompare(b, "ro"))
      .map((countyName) => ({
        countyName,
        locations: groups[countyName].sort((a, b) =>
          (a.name || "").localeCompare(b.name || "", "ro")
        ),
      }));
  };

  toggleCounty = (countyName) => {
    this.setState((prev) => ({
      expandedCounty: prev.expandedCounty === countyName ? null : countyName,
    }));
  };

  productById = (id) => (this.state.productsAll || []).find(p => p.id === id) || null;

  normalizePriceList(raw){
    const obj = raw?.priceList || raw || {};
    const id = obj?.iref || obj?.id;
    const withVAT = !!obj?.withVAT;
    const currency = obj?.currency===0?"RON":"RON";
    const prodLines = arrify(obj,["productPrice_list","productPrices"]).map(l=>({ product: typeof l.product==='object'?(l.product?.iref||l.product?.id):l.product, value:Number(l.value), isActive:l?.isActive!==false })).filter(l=>l.product && Number.isFinite(l.value));
    const comboLines = arrify(obj,["productCombo_list","comboPrices"]).map(l=>({ combo: typeof l.productCombo==='object'?(l.productCombo?.iref||l.productCombo?.id):l.productCombo, value:Number(l.value), isActive:l?.isActive!==false })).filter(l=>l.combo && Number.isFinite(l.value));
    const priceByProductId = {}; prodLines.forEach(l=>{ if(l.isActive) priceByProductId[l.product]=l.value; });
    const priceByComboId = {}; comboLines.forEach(l=>{ if(l.isActive) priceByComboId[l.combo]=l.value; });
    return { id, withVAT, currency, priceByProductId, priceByComboId };
  }

  // add params: effectiveProd, promoByProductId
  normalizeProducts(arr, pl, catMap, effectiveProd, promoByProductId, vatRatesById = {}){
    return (arr||[]).map(p=>{
      const id = p.iref || p.id || p.slug || p.ean || p.sku;
      const name = p.name || "";
      const categoryId = p.pCategory || p.category || null;
      const subcategoryId = p.pSubCategory || null;
      const { vatId, vatRate } = normalizeVatDefinition(
        p.vat,
        vatRatesById,
        p.vatRate ?? p.vatSell
      );
      const status = p.status;
      const isForSale = p.isForSale ?? p.isSellable ?? true;
      const isSoldAlone = p.isSoldAlone ?? true;
      const productImages = p.productImage_product || [];
      const imgMain = productImages.find(i=>i.isMain) || productImages[0];
      const imageSmallUrl = ensureSmallImage(imgMain?.image);
      const categoryName = catMap.get(categoryId)?.name || null;

      const basePrice = pl.priceByProductId[id];
      const effPrice  = effectiveProd ? (typeof effectiveProd === 'function' ? effectiveProd(id) : effectiveProd[id]) : basePrice;
      const isPromo   = promoByProductId ? (promoByProductId[id] != null) : false;

      const hasPrice = (effPrice != null); // effective price drives sellability
      const sellable = status===2 && isForSale===true;
      const showStandalone = sellable && hasPrice && isSoldAlone!==false;

      return {
        ...p,
        id,name,categoryId,subcategoryId,categoryName,vatId,vatRate,status,isForSale,isSoldAlone,
        sellable,hasPrice,showStandalone,imageSmallUrl,
        basePrice, effPrice, isPromo
      };
    });
  }

  // add params: effectiveCombo, promoByProductId
  normalizeCombos(arr, pl, effectiveCombo, promoByProductId, promoByComboId = {}, catMap = new Map()) {
    const asKey = (v) => String(v);
    const hasKey = (obj, k) =>
      Object.prototype.hasOwnProperty.call(obj || {}, asKey(k));
    const getMaybeMap = (m, k) =>
      m?.get ? m.get(asKey(k)) : m?.[asKey(k)];

    const priceByComboId = pl?.priceByComboId || {};
    const priceByProductId = pl?.priceByProductId || {};

    return (arr || []).map((c) => {
      const id = asKey(c.iref ?? c.id);
      const name = c.name || "";
      const shortName = c.shortName || name;
      const categoryId = c.pCategory || null;
      const subcategoryId = c.pSubCategory || null;
      const categoryName = catMap.get(categoryId)?.name || null;

      const basePricePL = priceByComboId[id];
      const ecValue = effectiveCombo ? getMaybeMap(effectiveCombo, id) : undefined;
      const basePriceEff = ecValue ?? basePricePL;

      const basePriceFromPL = basePricePL;
      const isComboPromo =
        typeof basePriceEff === "number" &&
        (basePriceFromPL == null || basePriceEff !== basePriceFromPL);

      const comboImages = c.comboImage_productCombo || [];
      const imgMain = comboImages.find(i=>i.isMain) || comboImages[0];
      const imageSmallUrl = ensureSmallImage(imgMain?.image);

      const cats = (c.comboCategory_productCombo || []).map((k) => ({
        id: asKey(k.iref ?? k.id),
        name: k.name,
        categoryId: k.productCategory?.iref || k.productCategory || null,
        subcategoryId: k.productSubCategory?.iref || k.productSubCategory || null,
        priceType: k.priceType ?? 0,
        priceValueC: toCents(k.priceValue || 0),
      }));

      const fixed = (c.combo_productComboProduct || []).map((p) => ({
        id: asKey(p.iref ?? p.id),
        name: p.name,
        productId: asKey(p.product?.iref ?? p.product),
        priceType: p.priceType ?? 0,
        priceValueC: toCents(p.priceValue || 0),
      }));

      const sellable = c.status === 2 && (c.isForSale ?? true) === true;

      const inPL = hasKey(priceByComboId, id);
      const inPromo = hasKey(promoByComboId, id);

      const allFixedPriced = fixed.every((fi) =>
        fi.priceType === 0 || fi.priceType === 1
          ? hasKey(priceByProductId, fi.productId) ||
            hasKey(promoByProductId, fi.productId)
          : true
      );

      const show = sellable && (inPL || inPromo) && allFixedPriced;

      if (!show) return null;

      const priceToUse = ecValue ?? basePricePL;

      return {
        id,
        name,
        shortName,
        categoryId,
        subcategoryId,
        categoryName,
        basePriceC: toCents(priceToUse ?? 0),
        basePricePL: basePriceFromPL ?? null,
        isComboPromo,
        imageSmallUrl,
        categories: cats,
        fixedItems: fixed,
      };
    }).filter(Boolean);
  }

  qtyForProduct = (id)=> (this.state.cartProducts[id]?.qty || 0);
  goHome = () => this.state.orderLocked ? null : this.setState({ view:"home", activeCategory:"All", search:"" });
  openCategory = (name) => this.state.orderLocked ? null : this.setState({ activeCategory: name, view:"browse" });

  ensureNameOrWarn = () => {
    /* const n = (this.state.customerName || "").trim();

    if (n.length < 2) {
      this.setState({
        view: "name",
        error: "Introdu numele pentru a continua.",
      });
      return false;
    } */

    return true;
  };

  getEffectiveMaps = (pl, promo) => {
    const promoByProductId = {};
    const promoByComboId   = {}; // future-proof if backend exposes combo promo lines
    const plist = promo?.promotionList || promo || {};
    const prodPromoLines = Array.isArray(plist.productPromotion_list) ? plist.productPromotion_list : [];
    prodPromoLines.forEach(x => {
      if (x?.isActive !== false && x?.product && Number.isFinite(Number(x.value))) {
        promoByProductId[x.product] = Number(x.value);
      }
    });
    // if you later expose comboPromotion_list, you can fill promoByComboId here

    const effectiveProd = { ...pl.priceByProductId };
    Object.keys(promoByProductId).forEach(pid => { effectiveProd[pid] = promoByProductId[pid]; });

    const effectiveCombo = { ...pl.priceByComboId };
    Object.keys(promoByComboId).forEach(cid => { effectiveCombo[cid] = promoByComboId[cid]; });

    return { promoByProductId, promoByComboId, effectiveProd, effectiveCombo };
  };

  priceSourceForCombo = (comboId) =>
    (this.state.promoByComboId[comboId] != null ? "promo" :
    this.state.priceList?.priceByComboId?.[comboId] != null ? "price" : null);

  // ——— price maps shortcuts
  getPriceContext = () => ({
    plByProd: this.state.priceList?.priceByProductId || {},
    priceListId: this.state.priceList?.id || null,
    promoByProd: this.state.promoByProductId || {},
    promoListId: this.state.promoList?.iref || null,
    // Optional: preload of other pricelists if you have them
    priceByProductByList: this.state.priceByProductByList || {}, // { [plId]: { [productId]: priceRON } }
  });

  getPriceFromList = (productId, listId) => {
    if (!listId) return null;
    const { priceByProductByList } = this.getPriceContext();
    const m = priceByProductByList[listId];
    return (m && m[productId] != null) ? Number(m[productId]) : null;
  };

  // Decide where a product’s price comes from given the combo slot type (0|1|2)
  priceSourceForProduct = (productId, slotType) => {
    const { plByProd, priceListId, promoByProd, promoListId } = this.getPriceContext();
    const hasPL    = (plByProd[productId] != null) || (this.getPriceFromList(productId, priceListId) != null);
    const hasPromo = !!promoListId && (promoByProd[productId] != null);

    if (slotType === 1) return hasPromo ? "promo" : (hasPL ? "pl" : null);
    if (slotType === 0) return hasPL ? "pl" : (hasPromo ? "promo" : null);   // anchor: PL first, else promo
    return hasPL ? "pl" : (hasPromo ? "promo" : null);                        // type‑2: prefer PL
  };

  // Which list id to annotate on a component (PL id or promo iref)
  priceListIdForProduct = (productId, slotType) => {
    const { priceListId, promoListId } = this.getPriceContext();
    const src = this.priceSourceForProduct(productId, slotType);
    return src === "promo" ? (promoListId || null) : (priceListId || null);
  };

  // *** The anchor used for type‑0 allocation (per product) ***
  anchorForType0 = (productId) => {
    const { plByProd, promoByProd } = this.getPriceContext();
    const src = this.priceSourceForProduct(productId, 0);

    if (src === "promo") {
      return Number(promoByProd[productId] ?? 0);
    }

    // src === "pl": try that product’s own PL id (if different), else current PL
    const compPLId = this.priceListIdForProduct(productId, 0);
    const vFromCompPL = this.getPriceFromList(productId, compPLId);
    if (vFromCompPL != null) return Number(vFromCompPL);

    const vCur = plByProd[productId];
    if (vCur != null) return Number(vCur);

    // final fallback
    return Number(promoByProd[productId] ?? 0);
  };

  addProduct = (p) => {
    if (this.state.orderLocked) return;
    if (!this.ensureNameOrWarn()) return;
    const effUnit = (this.state.promoByProductId?.[p.id] ?? this.state.priceList?.priceByProductId?.[p.id] ?? 0);
    const unitC = toCents(effUnit);
    const priceSource =
      (this.state.promoByProductId?.[p.id] != null) ? 'promo'
      : (this.state.priceList?.priceByProductId?.[p.id] != null) ? 'price'
      : null;

    this.setState(({ cartProducts }) => ({
      cartProducts: {
        ...cartProducts,
        [p.id]: {
          id: p.id,
          name: p.name,
          imageSmallUrl: p.imageSmallUrl,
          unitPriceC: unitC,
          qty: (cartProducts[p.id]?.qty || 0) + 1,
          vatId: p.vatId,
          vatRate: p.vatRate,
          priceSource,
        },
      },
    }));
  };

  incProduct = (id)=> this.state.orderLocked ? null : this.setState(({cartProducts})=>({ cartProducts:{...cartProducts,[id]:{...cartProducts[id], qty:(cartProducts[id]?.qty||0)+1}} }));
  decProduct = (id)=>{
    if (this.state.orderLocked) return;
    this.setState(({cartProducts})=>{ const line=cartProducts[id]; if(!line) return {cartProducts}; const next={...cartProducts}; if(line.qty<=1) delete next[id]; else next[id]={...line, qty: line.qty-1}; return {cartProducts: next}; });
  };

  openComboPicker = (combo)=> { if (this.state.orderLocked) return; if (!this.ensureNameOrWarn()) return; this.setState({ pickerOpen:true, pickerCombo:combo }); };
  closeComboPicker = ()=> this.setState({ pickerOpen:false, pickerCombo:null });

  confirmComboFromPicker = (selections, selectionDetails, meta) => {
    const { pickerCombo } = this.state;

    // attach fixed items + vatId from product catalog
    const fixedItems = (pickerCombo.fixedItems || []).map(fi => {
      const prod = this.productById(fi.productId);
      return {
        name: fi.name,
        productId: fi.productId,
        priceType: fi.priceType,        // 0/1/2
        priceValueC: fi.priceValueC,    // cents
        vatId: prod?.vatId || prod?.vat || null,
        vatRate: prod?.vatRate ?? null,
      };
    });

    const key = pickerCombo.id + '|' + Object.values(selections).map(x => (x.id || x)).sort().join(',');

    this.setState(({ cartCombos }) => ({
      cartCombos: {
        ...cartCombos,
        [key]: {
          id: pickerCombo.id,
          name: pickerCombo.name,
          imageSmallUrl: pickerCombo.imageSmallUrl,
          qty: (cartCombos[key]?.qty || 0) + 1,
          basePriceC: pickerCombo.basePriceC,
          selections,
          selectionDetails,
          fixedItems,
          priceSource: meta?.isComboPromo ? 'promo' : 'price', // 👈
        },
      },
      pickerOpen: false,
      pickerCombo: null,
    }));
  };

  incCombo = (key)=> this.state.orderLocked ? null : this.setState(({cartCombos})=>({ cartCombos:{...cartCombos,[key]:{...cartCombos[key], qty: cartCombos[key].qty+1}} }));
  decCombo = (key)=>{
    if (this.state.orderLocked) return;
    this.setState(({cartCombos})=>{ const line=cartCombos[key]; if(!line) return {cartCombos}; const next={...cartCombos}; if(line.qty<=1) delete next[key]; else next[key]={...line, qty: line.qty-1}; return {cartCombos: next}; });
  };
  clearCart = ()=> this.state.orderLocked ? null : this.setState({ cartProducts:{}, cartCombos:{} });

  vatRate = (vatId, explicitRate = null) =>
    normalizeVatDefinition(
      vatId,
      this.state.vatRatesById,
      explicitRate
    ).vatRate;

  requireVatRate = (item) => {
    const rate = this.vatRate(
      item?.vatId ?? item?.vat,
      item?.vatRate
    );
    if (rate == null) {
      throw new Error(this.t("skipline.vatUnavailable"));
    }
    return rate;
  };

  round2(n) {
    return Math.round(Number(n) * 100) / 100;
  }

  allocType0PerUnitRON(basePriceRON, type0, priceList) {
    const P0 = type0.map(s => {
      const plv = Number(this.state.priceList?.priceByProductId?.[s.productId] ?? 0);
      if (plv > 0) return plv;
      const pv = Number(this.state.promoByProductId?.[s.productId] ?? 0);
      return pv > 0 ? pv : 0;
    });
    const S0 = P0.reduce((a, b) => a + b, 0);

    if (!type0?.length) return [];
    if (S0 <= 0) {
      // Even split if no standalone prices available
      const each = this.round2(basePriceRON / type0.length);
      const out = Array(type0.length).fill(each);
      const drift = this.round2(basePriceRON - out.reduce((a, b) => this.round2(a + b), 0));
      // add/pad the remainder (+/-0.01) to the first item
      if (Math.abs(drift) >= 0.01) out[0] = this.round2(out[0] + drift);
      return out;
    }

    // Proportional round per component
    const raw = P0.map(p => this.round2(basePriceRON * (p / S0)));
    let sum = this.round2(raw.reduce((a, b) => this.round2(a + b), 0));
    let rem = this.round2(basePriceRON - sum); // can be -0.01, 0, or +0.01

    if (Math.abs(rem) >= 0.01) {
      // give/take the cent to the product with the largest P0 (most significant)
      const idx = P0.indexOf(Math.max(...P0));
      raw[idx] = this.round2(raw[idx] + rem);
    }
    return raw;
  }

  computeTotals(){
    const { cartProducts, cartCombos } = this.state;
    let totalGrossC = 0, totalVatC = 0;

    // ----- products -----
    Object.values(cartProducts).forEach(l => {
      const unit = l.unitPriceC;                // cents
      const qty  = l.qty;
      const rate = this.vatRate(l.vatId, l.vatRate) ?? 0; // %
      const lineC = unit * qty;
      const vatC  = Math.round(lineC * rate / (100 + rate));
      totalGrossC += lineC;
      totalVatC   += vatC;
    });

    // ----- combos -----
    Object.values(cartCombos).forEach(line => {
      const { basePriceC, qty, selectionDetails = [], fixedItems = [] } = line;

      const allItems = [
        ...selectionDetails,
        ...fixedItems.map(fi => ({
          productId: fi.productId,
          priceType: fi.priceType,
          priceValueC: fi.priceValueC,
          vatId: fi.vatId,
          vatRate: fi.vatRate,
          name: fi.name,
        })),
      ];

      const type1 = allItems.filter(s => s.priceType === 1);
      const type2 = allItems.filter(s => s.priceType === 2);
      const type0 = allItems.filter(s => s.priceType === 0);

      // per‑unit Σ(type‑1) from effective product prices (promo > list), cents
      const effProdMap = this.state.effectiveProd || {};
      const v1 = type1.map(s => s.productId
        ? toCents(((typeof effProdMap === "function") ? effProdMap(s.productId) : effProdMap[s.productId]) || 0)
        : 0
      );
      const sum1 = v1.reduce((a, b) => a + b, 0);

      // per‑unit Σ(type‑2) from set values, cents
      const v2 = type2.map(s => s.priceValueC || 0);
      const sum2 = v2.reduce((a, b) => a + b, 0);

      // per‑unit full line value
      const unitLineC = (basePriceC || 0) + sum1 + sum2;

      // allocate BASE ONLY (basePriceC) to type‑0 using pricelist proportions (RON per unit)
      const P0 = type0.map(s => this.anchorForType0(s.productId));
      const S0 = P0.reduce((a, b) => a + b, 0);
      const baseUnitRON = fromCents(basePriceC || 0);

      let alloc0UnitRON = [];
      if (type0.length) {
        if (S0 <= 0) {
          const each = Math.round((baseUnitRON / type0.length) * 100) / 100;
          alloc0UnitRON = Array(type0.length).fill(each);
          const drift = Math.round((baseUnitRON - alloc0UnitRON.reduce((a,b)=>Math.round((a+b)*100)/100,0)) * 100)/100;
          if (Math.abs(drift) >= 0.01) alloc0UnitRON[0] = Math.round((alloc0UnitRON[0] + drift) * 100)/100;
        } else {
          alloc0UnitRON = P0.map(p => Math.round((baseUnitRON * (p / S0)) * 100) / 100);
          let sum = Math.round(alloc0UnitRON.reduce((a,b)=>Math.round((a+b)*100)/100,0) * 100)/100;
          let rem = Math.round((baseUnitRON - sum) * 100) / 100;
          if (Math.abs(rem) >= 0.01) {
            const idx = P0.indexOf(Math.max(...P0));
            alloc0UnitRON[idx] = Math.round((alloc0UnitRON[idx] + rem) * 100) / 100;
          }
        }
      }

      // VAT per unit from each component
      let unitVatC = 0;
      type0.forEach((s, i) => {
        const rate = this.vatRate(s.vatId, s.vatRate) ?? 0;
        unitVatC += Math.round(toCents(alloc0UnitRON[i]) * rate / (100 + rate));
      });
      type1.forEach((s, i) => {
        const rate = this.vatRate(s.vatId, s.vatRate) ?? 0;
        unitVatC += Math.round(v1[i] * rate / (100 + rate));
      });
      type2.forEach((s, i) => {
        const rate = this.vatRate(s.vatId, s.vatRate) ?? 0;
        unitVatC += Math.round(v2[i] * rate / (100 + rate));
      });

      totalGrossC += unitLineC * qty;
      totalVatC   += unitVatC * qty;
    });

    return {
      totalGross: fromCents(totalGrossC),
      totalVat:   fromCents(totalVatC),
      totalNet:   fromCents(totalGrossC - totalVatC),
    };
  }

  buildOrderPayload(
    orderContext = ORDER_REQUEST_CONTEXT.guest
  ) {
    const { priceList, cartProducts, cartCombos, guestEmail, promoList } = this.state;

    const orderProductEntry_order = [];
    const orderProductPromotionEntry_order = [];

    Object.values(cartProducts).forEach((l) => {
      const unitC = l.unitPriceC;
      const qty = l.qty || 1;
      const rate = this.requireVatRate(l);

      const lineC = unitC * qty;
      const lineVat = Math.round(lineC * rate / (100 + rate));

      const common = {
        value: fromCents(lineC),
        quantity: qty,
        vat: fromCents(lineVat),
      };

      const isPromoProduct =
        !!(promoList?.iref) &&
        this.state.promoByProductId?.[l.id] != null;

      if (isPromoProduct) {
        orderProductPromotionEntry_order.push({
          ...common,
          ProductPromotion: {
            promotionList: this.state.promoList.iref,
            product: l.id,
            value: fromCents(unitC),
          },
        });
      } else {
        orderProductEntry_order.push({
          ...common,
          ProductPrice: {
            priceList: priceList.id,
            product: l.id,
            value: fromCents(unitC),
          },
        });
      }
    });

    const orderComboEntry_order = [];
    const orderComboPromotionEntry_order = [];

    Object.values(cartCombos).forEach((line) => {
      const { basePriceC, qty = 1, selectionDetails = [], id, fixedItems = [] } = line;

      const allItems = [
        ...selectionDetails,
        ...fixedItems.map((fi) => {
          const prod = this.productById(fi.productId);
          return {
            productId: fi.productId,
            priceType: fi.priceType,
            priceValueC: fi.priceValueC,
            vatId: prod?.vatId || prod?.vat || null,
            vatRate: prod?.vatRate ?? fi.vatRate ?? null,
            name: fi.name,
          };
        }),
      ];

      const type1 = allItems.filter((s) => s.priceType === 1);
      const type2 = allItems.filter((s) => s.priceType === 2);
      const type0 = allItems.filter((s) => s.priceType === 0);

      const effProdMap = this.state.effectiveProd || {};
      const v1 = type1.map((s) =>
        s.productId
          ? toCents(
              ((typeof effProdMap === "function")
                ? effProdMap(s.productId)
                : effProdMap[s.productId]) || 0
            )
          : 0
      );

      const sum1 = v1.reduce((a, b) => a + b, 0);

      const v2 = type2.map((s) => s.priceValueC || 0);
      const sum2 = v2.reduce((a, b) => a + b, 0);

      const unitLineC = (basePriceC || 0) + sum1 + sum2;
      const unitLineRON = fromCents(unitLineC);

      const P0 = type0.map(s => this.anchorForType0(s.productId));
      const S0 = P0.reduce((a, b) => a + b, 0);
      const baseUnitRON = fromCents(basePriceC || 0);

      let alloc0UnitRON = [];

      if (type0.length) {
        if (S0 <= 0) {
          const each = Math.round((baseUnitRON / type0.length) * 100) / 100;
          alloc0UnitRON = Array(type0.length).fill(each);

          const drift =
            Math.round(
              (
                baseUnitRON -
                alloc0UnitRON.reduce((a,b)=>Math.round((a+b)*100)/100,0)
              ) * 100
            ) / 100;

          if (Math.abs(drift) >= 0.01) {
            alloc0UnitRON[0] = Math.round((alloc0UnitRON[0] + drift) * 100) / 100;
          }
        } else {
          alloc0UnitRON = P0.map(p => Math.round((baseUnitRON * (p / S0)) * 100) / 100);

          let sum =
            Math.round(
              alloc0UnitRON.reduce((a,b)=>Math.round((a+b)*100)/100,0) * 100
            ) / 100;

          let rem = Math.round((baseUnitRON - sum) * 100) / 100;

          if (Math.abs(rem) >= 0.01) {
            const idx = P0.indexOf(Math.max(...P0));
            alloc0UnitRON[idx] = Math.round((alloc0UnitRON[idx] + rem) * 100) / 100;
          }
        }
      }

      const components = [];

      type1.forEach((s, i) => {
        const perUnitRON = fromCents(v1[i]);
        const valRON = Math.round(perUnitRON * qty * 100) / 100;
        const rate = this.requireVatRate(s);
        const vVat = Math.round((valRON * rate / (100 + rate)) * 100) / 100;

        components.push({
          value: valRON,
          quantity: qty,
          vat: vVat,
          product: s.productId,
          priceType: 1,
          priceList: this.priceListIdForProduct(s.productId),
        });
      });

      type2.forEach((s, i) => {
        const perUnitRON = fromCents(v2[i]);
        const valRON = Math.round(perUnitRON * qty * 100) / 100;
        const rate = this.requireVatRate(s);
        const vVat = Math.round((valRON * rate / (100 + rate)) * 100) / 100;

        components.push({
          value: valRON,
          quantity: qty,
          vat: vVat,
          product: s.productId,
          priceType: 2,
          priceList: this.priceListIdForProduct(s.productId),
        });
      });

      type0.forEach((s, i) => {
        const valRON = Math.round(alloc0UnitRON[i] * qty * 100) / 100;
        const rate = this.requireVatRate(s);
        const vVat = Math.round((valRON * rate / (100 + rate)) * 100) / 100;

        components.push({
          value: valRON,
          quantity: qty,
          vat: vVat,
          product: s.productId,
          priceType: 0,
          priceList: this.priceListIdForProduct(s.productId),
        });
      });

      const lineValueRON = Math.round(unitLineRON * qty * 100) / 100;
      const lineVatRON =
        Math.round(
          components.reduce((a, b) => a + Number(b.vat || 0), 0) * 100
        ) / 100;

      const lineCommon = {
        value: lineValueRON,
        quantity: qty,
        vat: lineVatRON,
      };

      const usePromoCombo =
        !!(promoList?.iref) &&
        this.state.promoByComboId?.[id] != null;

      if (usePromoCombo) {
        orderComboPromotionEntry_order.push({
          ...lineCommon,
          ComboPromotion: {
            promotionList: this.state.promoList.iref,
            productCombo: id,
            value: fromCents(basePriceC),
          },
          OrderComboPromotionProductsEntry_OrderComboPromotionEntry: components,
        });
      } else {
        orderComboEntry_order.push({
          ...lineCommon,
          ComboPrice: {
            priceList: this.state.priceList.id,
            productCombo: id,
            value: fromCents(basePriceC),
          },
          OrderComboProductsEntry_OrderComboEntry: components,
        });
      }
    });

    const allLines = [
      ...orderProductEntry_order,
      ...orderProductPromotionEntry_order,
      ...orderComboEntry_order,
      ...orderComboPromotionEntry_order,
    ];

    const grossC = allLines.reduce((a, x) => a + cents(x.value), 0);
    const vatC = allLines.reduce((a, x) => a + cents(x.vat), 0);

    const cleanEmail = String(guestEmail || "").trim().toLowerCase();

    const brandName = this.getBrandName();
    const descriptionBase =
      `Comanda ${this.state.locationInfo?.name || brandName}`;

    const payload = {
      type: 1,
      status: 0,
      deliveryType: 0,
      description: this.isClientOrderContext(orderContext)
        ? descriptionBase
        : `${descriptionBase} - ${cleanEmail}`,
      currency: 0,
      location: this.state.location,

      numberOfLines:
        orderProductEntry_order.length +
        orderProductPromotionEntry_order.length +
        orderComboEntry_order.length +
        orderComboPromotionEntry_order.length,

      totalAmount: fromc(grossC),
      totalAmountWithoutVAT: fromc(grossC - vatC),
      discountValue: 0,
      discountValueWithoutVAT: 0,

      transaction_order: [
        {
          type: 0,
          status: 0,
          platform: 2,
          amount: fromc(grossC),
        },
      ],

      orderProductEntry_order,
      orderComboEntry_order,
      orderProductPromotionEntry_order,
      orderComboPromotionEntry_order,
    };

    const v = validateOrderPayload(payload, this.state.effectiveProd || {});
    if (!v.ok) {
      throw new Error(v.errors.join(" · "));
    }

    return payload;
  }

  openPayU = () => {
    const payUrl = toSafePaymentRedirectUrl(this.state.payUrl);
    if (!payUrl) {
      this.setState({ error: this.t("skipline.noPaymentLink") });
      return;
    }
    window.open(payUrl, "_blank", "noopener,noreferrer");
    this.setState({
      paymentChecking: true,
      paymentMessage: this.tr("payment.cardOnlyProvider", { provider: this.state.paymentProvider }),
    }, this.startPolling);
  };

  startPolling = () => { this.stopPolling(); this.pollTimer = setInterval(this.checkOrderStatus, 5000); };
  stopPolling = () => { if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; } };

  isClientOrderContext = (context) =>
    context === ORDER_REQUEST_CONTEXT.client;

  getOrderRequestContext = () =>
    selectOrderRequestContext({
      authenticatedCheckoutEnabled:
        this.props.authenticatedCheckoutEnabled,
      isAuthenticated: this.props.clientPortalAuthenticated,
    });

  isClientPortalBusy = () =>
    Boolean(
      this.props.clientPortalBusy ??
        this.props.clientPortalRestoring
    );

  createOrderForContext = async (context, payload) => {
    if (this.isClientOrderContext(context)) {
      return this.props.addAuthenticatedOrder(payload);
    }
    await this.ensurePublicSession();
    return this.api.post(ENDPOINTS.createOrder(), payload);
  };

  registerPayForContext = (context, orderId, guestEmail) => {
    if (this.isClientOrderContext(context)) {
      return this.props.registerAuthenticatedPay(orderId);
    }
    return this.api.post(ENDPOINTS.registerPay(), {
      orderId,
      order: orderId,
      iref: orderId,
      email: guestEmail,
      guestEmail,
    });
  };

  registerKnownOrder = async (orderId, context, guestEmail) => {
    if (this.registerPayPromise) return this.registerPayPromise;

    const pending = (async () => {
      this.setState({
        placing: true,
        paymentRegistering: true,
        paymentRegistrationFailed: false,
        clientSessionRequired: false,
        error: "",
      });
      try {
        const payRes = await this.registerPayForContext(
          context,
          orderId,
          guestEmail
        );
        const payUrl = extractRedirectPaymentUrl(payRes, context);
        if (!payUrl) throw new Error(this.t("skipline.noPaymentLink"));

        this.setState({
          placing: false,
          paymentRegistering: false,
          paymentRegistrationFailed: false,
          payUrl,
          paymentSheetOpen: true,
          paymentMessage: this.t("skipline.orderRegistered"),
          paymentChecking: false,
          payIframeError: true,
          cartOpen: false,
        });
        return true;
      } catch (error) {
        const clientSessionRequired =
          this.isClientOrderContext(context) && error?.status === 401;
        this.setState({
          placing: false,
          paymentRegistering: false,
          paymentRegistrationFailed: true,
          clientSessionRequired,
          payUrl: "",
          paymentSheetOpen: true,
          paymentChecking: false,
          payIframeError: true,
          paymentMessage: clientSessionRequired
            ? this.t("skipline.clientSessionRequired")
            : this.t("skipline.paymentRegistrationFailed"),
          error: clientSessionRequired
            ? this.t("skipline.clientSessionRequired")
            : this.t("skipline.paymentRegistrationFailed"),
        });
        return false;
      }
    })();

    this.registerPayPromise = pending;
    try {
      return await pending;
    } finally {
      if (this.registerPayPromise === pending) {
        this.registerPayPromise = null;
      }
    }
  };

  retryPaymentRegistration = async () => {
    const order = this.pendingCreatedOrder;
    if (!order || this.state.paymentRegistering) return;
    await this.registerKnownOrder(order.id, order.context, order.guestEmail);
  };

  restoreClientSession = async (credentials) => {
    const result = await this.props.loginClient(credentials);
    if (!result?.ok) return result;
    const hasKnownOrder = Boolean(this.pendingCreatedOrder);
    this.setState({
      clientSessionRequired: false,
      ...(hasKnownOrder ? {} : {
        paymentSheetOpen: false,
        error: "",
      }),
    });
    this.props.refreshAfterOrderOutcome();
    if (this.state.paymentRegistrationFailed) {
      this.retryPaymentRegistration();
    }
    return result;
  };

  logoutClientBeforeOrder = () => {
    if (
      this.state.orderLocked ||
      this.state.placing ||
      this.orderAddInFlight ||
      !this.props.logoutClient
    ) {
      return;
    }
    this.setState({
      clientSessionRequired: false,
      error: "",
    });
    Promise.resolve(this.props.logoutClient()).catch(() => {
      // The context has already completed the authoritative local logout.
    });
  };

  checkout = async () => {
    if (this.state.placing || this.orderAddInFlight) return;

    if (
      this.props.authenticatedCheckoutEnabled &&
      this.isClientPortalBusy()
    ) {
      this.setState({ error: this.t("skipline.clientSessionRestoring") });
      return;
    }

    const cartQty =
      Object.values(this.state.cartProducts || {}).reduce((a, l) => a + (l?.qty || 0), 0) +
      Object.values(this.state.cartCombos || {}).reduce((a, l) => a + (l?.qty || 0), 0);

    if (cartQty === 0) {
      this.setState({ error: this.t("skipline.addItems") });
      return;
    }

    const orderContext = this.getOrderRequestContext();
    const isClientOrder = this.isClientOrderContext(orderContext);
    const guestEmail = isClientOrder
      ? ""
      : String(this.state.guestEmail || "").trim().toLowerCase();

    if (
      isClientOrder &&
      this.props.clientProfile?.hasOrderEmail !== true
    ) {
      this.setState({
        error: this.t("skipline.clientAccountEmailMissing"),
      });
      return;
    }

    if (!isClientOrder && !isValidEmail(guestEmail)) {
      this.setState({
        view: "name",
        error: this.t("skipline.enterPaymentEmail"),
      });
      return;
    }

    let payload;

    try {
      payload = this.buildOrderPayload(orderContext);
    } catch (err) {
      this.setState({
        error: explainBackendError(err) || String(err?.message || err),
      });
      return;
    }

    const totalLines =
      (payload.orderProductEntry_order?.length || 0) +
      (payload.orderProductPromotionEntry_order?.length || 0) +
      (payload.orderComboEntry_order?.length || 0) +
      (payload.orderComboPromotionEntry_order?.length || 0);

    if (totalLines === 0) {
      this.setState({ error: this.t("skipline.addItems") });
      return;
    }

    if (
      isClientOrder &&
      (!this.state.paymentProviderConfirmed ||
        !["PayU", "BT"].includes(this.state.paymentProvider))
    ) {
      this.setState({
        error: this.t("skipline.authenticatedPaymentUnavailable"),
      });
      return;
    }

    if (isClientOrder) {
      try {
        payload = adaptAuthenticatedOrderPayload(payload);
      } catch (error) {
        this.setState({
          error: this.t("skipline.clientBenefitsUnavailable"),
        });
        return;
      }
    }

    this.orderAddInFlight = true;
    this.setState({ placing: true, error: "" });

    try {
      const res = await this.createOrderForContext(orderContext, payload);
      const orderId = extractCreatedOrderId(res, orderContext);

      if (!orderId) {
        throw new Error(this.t("skipline.orderError"));
      }

      const totals = this.computeTotals();
      const serverTotal = extractServerOrderTotal(res);
      const lastOrderGross =
        serverTotal ?? totals.totalGross ?? 0;
      this.pendingCreatedOrder = {
        id: orderId,
        context: orderContext,
        guestEmail,
      };

      this.setState({
        orderLocked: true,
        lastOrderId: orderId,
        lastOrderGross,
        lastOrderContext: orderContext,
        cartOpen: false,
      });

      if (serverTotal === 0) {
        this.setState({
          placing: false,
          paymentRegistering: false,
          paymentRegistrationFailed: false,
          paymentSheetOpen: false,
          summaryOpen: true,
        });
        if (this.isClientOrderContext(orderContext)) {
          this.props.refreshAfterOrderOutcome();
        }
        return;
      }

      await this.registerKnownOrder(orderId, orderContext, guestEmail);
    } catch (e) {
      const clientSessionRequired =
        this.isClientOrderContext(orderContext) && e?.status === 401;
      this.setState({
        placing: false,
        clientSessionRequired,
        paymentSheetOpen: clientSessionRequired,
        payIframeError: clientSessionRequired,
        paymentRegistrationFailed: false,
        error: clientSessionRequired
          ? this.t("skipline.clientSessionRequired")
          : this.t("skipline.orderAddUncertain"),
      });
    } finally {
      this.orderAddInFlight = false;
    }
  };

  checkOrderStatus = async () => {
    const {
      lastOrderId,
      lastOrderContext = ORDER_REQUEST_CONTEXT.guest,
    } = this.state;
    if (!lastOrderId) return;

    try {
      this.setState({ paymentChecking: true });

      const statusRes = this.isClientOrderContext(lastOrderContext)
        ? await this.props.getAuthenticatedPayStatus(lastOrderId)
        : await this.api.get(ENDPOINTS.orderPayStatus(lastOrderId));

      const stRaw =
        statusRes?.data?.orderStatus ??
        statusRes?.data?.status ??
        statusRes?.orderStatus ??
        statusRes?.status;

      const st = typeof stRaw === "string" ? parseInt(stRaw, 10) : stRaw;

      if ([1, 5, 6, 7].includes(st)) {
        this.stopPolling();

        this.setState({
          paymentChecking: false,
          paymentMessage: this.isClientOrderContext(lastOrderContext)
            ? this.t("skipline.paymentSuccess")
            : statusRes?.message || this.t("skipline.paymentSuccess"),
        });
        if (this.isClientOrderContext(lastOrderContext)) {
          this.props.refreshAfterOrderOutcome();
        }

        setTimeout(() => {
          this.setState({
            paymentSheetOpen: false,
            summaryOpen: true,
          });
        }, 400);

        return;
      }

      if (
        st === 0 ||
        st === 4 ||
        (!this.isClientOrderContext(lastOrderContext) && st == null)
      ) {
        this.setState({
          paymentChecking: false,
          paymentMessage:
            this.t("skipline.paymentPending"),
        });
        return;
      }

      if (st === 3) {
        this.stopPolling();

        this.setState({
          paymentChecking: false,
          paymentMessage: this.t("skipline.paymentCanceled"),
        });
        if (this.isClientOrderContext(lastOrderContext)) {
          this.props.refreshAfterOrderOutcome();
        }

        return;
      }

      if ([2, 8].includes(st)) {
        this.stopPolling();
        if (this.isClientOrderContext(lastOrderContext)) {
          this.props.refreshAfterOrderOutcome();
        }
        this.setState({
          paymentChecking: false,
          paymentMessage: this.t("skipline.paymentCheckFailed"),
        });
        return;
      }

      if (!this.isClientOrderContext(lastOrderContext)) {
        this.setState({
          paymentChecking: false,
          paymentMessage: this.tr("skipline.paymentStatus", {
            status: String(stRaw),
          }),
        });
        return;
      }
      this.setState({
        paymentChecking: false,
        paymentMessage: this.t("skipline.paymentCheckFailed"),
      });
    } catch (e) {
      const clientSessionRequired =
        this.isClientOrderContext(lastOrderContext) && e?.status === 401;
      if (clientSessionRequired) this.stopPolling();
      this.setState({
        paymentChecking: false,
        clientSessionRequired,
        paymentMessage: clientSessionRequired
          ? this.t("skipline.clientSessionRequired")
          : this.t("skipline.paymentCheckFailed"),
      });
    }
  };

  cancelCurrentOrder = async () => {
    const {
      lastOrderId,
      lastOrderContext = ORDER_REQUEST_CONTEXT.guest,
    } = this.state;
    if (!lastOrderId) return;

    try {
      // stop polling first
      this.stopPolling();

      // POST to cancel
      if (this.isClientOrderContext(lastOrderContext)) {
        await this.props.cancelAuthenticatedOrder(lastOrderId);
      } else {
        await this.api.post(ENDPOINTS.cancelOrder(lastOrderId), {});
      }
      if (this.isClientOrderContext(lastOrderContext)) {
        this.props.refreshAfterOrderOutcome();
      }

      // reset UI so the user can start over
      this.setState({
        paymentSheetOpen: false,
        paymentMessage: "",
        paymentChecking: false,
        payUrl: "",
        orderLocked: false,
        lastOrderId: null,
        lastOrderGross: null,
        lastOrderContext: null,
        paymentRegistrationFailed: false,
        paymentRegistering: false,
        clientSessionRequired: false,
        cartProducts: {},
        cartCombos: {},
        infoProduct: null,
        view: "name",
        activeCategory: "All",
        search: "",
        error: "",
      });
      this.pendingCreatedOrder = null;
    } catch (e) {
      const clientSessionRequired =
        this.isClientOrderContext(lastOrderContext) && e?.status === 401;
      this.setState({
        clientSessionRequired,
        error: clientSessionRequired
          ? this.t("skipline.clientSessionRequired")
          : this.t("skipline.cancelFailed"),
      });
    }
  };

  renderLocationSelect() {
    const { selectingLocation, locationList, expandedCounty, locationSearch } = this.state;
    const groupedLocations = this.groupLocationsByCounty();
    const hasSearch = String(locationSearch || "").trim().length > 0;
    const brandNameUpper = this.getBrandNameUpper();

    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: PAGE_BG,
        }}
      >
        <AppBar
          position="fixed"
          elevation={0}
          sx={appBarSx}
        >
          <Toolbar sx={{ minHeight: 64 }}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              {this.renderBrandMark()}

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 900,
                    letterSpacing: 0.3,
                    lineHeight: 1.1,
                    visibility: brandNameUpper ? "visible" : "hidden",
                  }}
                  noWrap
                >
                  {brandNameUpper || DEFAULT_BRAND_NAME.toLocaleUpperCase("ro-RO")}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.25 }}
                  noWrap
                >
                  {this.t("skipline.tagline")}
                </Typography>
              </Box>
            </Stack>

            <Box sx={{ flex: 1 }} />

            {this.renderLanguageSwitcher()}
          </Toolbar>
        </AppBar>

        <Container
          maxWidth="sm"
          sx={{
            pt: { xs: 36, sm: 32, md: 32 },
            pb: { xs: 2.5, sm: 4 },
          }}
        >
          <Box
            sx={{
              position: "fixed",
              top: 64,
              left: 0,
              right: 0,
              zIndex: 1090,
              bgcolor: PAGE_BG,
              px: 2,
              py: 1,
              borderBottom: "1px solid",
              borderColor: BORDER,
            }}
          >
            <Container maxWidth="sm" disableGutters>
              <Card
                elevation={0}
                sx={{
                  borderRadius: "8px",
                  border: "1px solid",
                  borderColor: BORDER,
                  boxShadow: "0 14px 34px rgba(33,20,12,0.08)",
                  background: "linear-gradient(180deg, #fffaf3 0%, #ffffff 72%)",
                }}
              >
                <Box sx={{ p: 1.5 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 900,
                      mb: 0.25,
                    }}
                  >
                    {this.t("skipline.chooseLocation")}
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      mb: 1.25,
                    }}
                  >
                    {this.t("skipline.chooseLocationDesc")}
                  </Typography>

                  <TextField
                    fullWidth
                    size="small"
                    placeholder={this.t("skipline.searchLocation")}
                    value={locationSearch}
                    onChange={(e) => this.setState({ locationSearch: e.target.value })}
                    inputProps={{
                      "aria-label": this.t("skipline.searchLocation"),
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: locationSearch ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => this.setState({ locationSearch: "" })}
                            aria-label={this.t("skipline.clearFilter")}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        bgcolor: "#fffaf3",
                      },
                    }}
                  />

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      mt: 1.25,
                      lineHeight: 1.4,
                    }}
                  >
                    {this.tr("skipline.termsPrefix", {
                      brand: this.getBrandName(),
                      provider: this.state.paymentProvider,
                    })}{" "}
                    <Box
                      component="a"
                      href={withAppBasePath("/terms")}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: "primary.main",
                        fontWeight: 700,
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {this.t("skipline.terms")}
                    </Box>
                    {" "}{this.t("skipline.and")}{" "}
                    <Box
                      component="a"
                      href={withAppBasePath("/privacy")}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: "primary.main",
                        fontWeight: 700,
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {this.t("skipline.privacy")}
                    </Box>
                    .
                  </Typography>
                </Box>
              </Card>
            </Container>
          </Box>

          <Card
            elevation={0}
            sx={{
              ...compactCardSx,
            }}
          >
            <Box sx={{ p: 1.5 }}>
              {selectingLocation ? (
                <Box sx={{ py: 4 }}>
                  <Stack spacing={1.25}>
                    {[1, 2, 3].map((x) => (
                      <Skeleton
                        key={x}
                        variant="rounded"
                        height={74}
                        sx={{ borderRadius: "8px" }}
                      />
                    ))}
                  </Stack>
                </Box>
              ) : locationList.length === 0 ? (
                <Alert severity="warning" sx={{ borderRadius: "8px" }}>
                  {this.t("skipline.noLocations")}
                </Alert>
              ) : groupedLocations.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: "8px" }}>
                  {this.t("skipline.noSearchLocations")}
                </Alert>
              ) : (
                <Stack spacing={1.25}>
                  {groupedLocations.map((group) => {
                    const isOpen = hasSearch || expandedCounty === group.countyName;
                    const countyPanelId = `skipline-county-${String(
                      group.countyName
                    ).replace(/[^a-z0-9_-]+/gi, "-")}`;

                    return (
                      <Box
                        key={group.countyName}
                        sx={{
                          border: "1px solid",
                          borderColor: isOpen ? "primary.main" : BORDER,
                          borderRadius: "8px",
                          bgcolor: "background.paper",
                          overflow: "hidden",
                          transition: "border-color 150ms ease, box-shadow 150ms ease",
                          boxShadow: isOpen
                            ? "0 10px 26px rgba(247, 181, 0, 0.14)"
                            : "none",
                        }}
                      >
                        <Button
                          fullWidth
                          onClick={() => this.toggleCounty(group.countyName)}
                          aria-expanded={isOpen}
                          aria-controls={countyPanelId}
                          sx={{
                            justifyContent: "space-between",
                            textAlign: "left",
                            px: 2,
                            py: 1.6,
                            color: "text.primary",
                          }}
                        >
                          <Stack direction="row" spacing={1.25} alignItems="center">
                            <Box
                              sx={{
                                width: 38,
                                height: 38,
                                borderRadius: "8px",
                                bgcolor: isOpen ? "primary.main" : "#fff7df",
                                color: isOpen ? "primary.contrastText" : "text.secondary",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <LocationOnRoundedIcon fontSize="small" />
                            </Box>

                            <Box>
                              <Typography sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                                {group.countyName}
                              </Typography>

                              <Typography variant="caption" color="text.secondary">
                                {group.locations.length}{" "}
                                {group.locations.length === 1
                                  ? this.t("skipline.locationOne")
                                  : this.t("skipline.locationMany")}
                              </Typography>
                            </Box>
                          </Stack>

                          <Typography
                            sx={{
                              fontWeight: 900,
                              fontSize: 18,
                              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                              transition: "transform 150ms ease",
                              color: "text.secondary",
                            }}
                          >
                            ▾
                          </Typography>
                        </Button>

                        {isOpen ? (
                          <Box
                            id={countyPanelId}
                            sx={{
                              px: 1.25,
                              pb: 1.25,
                            }}
                          >
                            <Stack spacing={1}>
                              {group.locations.map((loc) => {
                                const addressText = this.formatAddress(loc.address);

                                return (
                                  <Button
                                    key={loc.iref}
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => this.selectLocation(loc.iref)}
                                    sx={{
                                      justifyContent: "space-between",
                                      textAlign: "left",
                                      py: 1.3,
                                      px: 1.5,
                                      borderRadius: "8px",
                                      borderColor: BORDER,
                                      color: "text.primary",
                                      bgcolor: "#fff",
                                      "&:hover": {
                                        borderColor: "primary.main",
                                        bgcolor: "rgba(247, 181, 0, 0.08)",
                                      },
                                    }}
                                  >
                                    <Box sx={{ minWidth: 0, pr: 1 }}>
                                      <Typography
                                        sx={{
                                          fontWeight: 800,
                                          lineHeight: 1.25,
                                        }}
                                      >
                                        {loc.name}
                                      </Typography>

                                      {addressText ? (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{
                                            display: "block",
                                            mt: 0.35,
                                            whiteSpace: "normal",
                                            lineHeight: 1.35,
                                          }}
                                        >
                                          {addressText}
                                        </Typography>
                                      ) : null}
                                    </Box>

                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 800,
                                        color: "primary.main",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {this.t("skipline.choose")}
                                    </Typography>
                                  </Button>
                                );
                              })}
                            </Stack>
                          </Box>
                        ) : null}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Card>
        </Container>
      </Box>
    );
  }

  renderProductCard = (p) => {
    const price = p.effPrice ?? this.state.priceList?.priceByProductId?.[p.id] ?? null;
    const base = p.basePrice;
    const promo = !!p.isPromo;
    const qty = this.qtyForProduct(p.id);
    const locked = this.state.orderLocked;

    return (
      <Card
        elevation={0}
        sx={{
          ...compactCardSx,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderColor: qty > 0 ? "primary.main" : BORDER,
          bgcolor: qty > 0 ? "rgba(255,253,247,0.98)" : "background.paper",
          opacity: locked ? 0.55 : 1,
          boxShadow: qty > 0
            ? "0 18px 42px rgba(247, 181, 0, 0.20)"
            : compactCardSx.boxShadow,
          transition: "transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease, background-color 140ms ease",
          "&:hover": locked
            ? {}
            : {
                transform: "translateY(-2px)",
                boxShadow: "0 16px 36px rgba(33,20,12,0.12)",
                borderColor: qty > 0 ? "primary.main" : "rgba(247, 181, 0, 0.45)",
              },
          "&:focus-within": {
            borderColor: qty > 0 ? "primary.main" : "rgba(247, 181, 0, 0.55)",
            boxShadow: "0 0 0 3px rgba(247,181,0,0.16), 0 16px 36px rgba(33,20,12,0.10)",
          },
          "&:hover .skipline-product-media": locked
            ? {}
            : {
                transform: "scale(1.035)",
                filter: "saturate(1.05)",
              },
        }}
      >
        <Box
          sx={{
            position: "relative",
            height: { xs: 132, sm: 148 },
            bgcolor: "rgba(255,250,243,0.94)",
            borderBottom: "1px solid",
            borderColor: "rgba(124,45,18,0.08)",
            overflow: "hidden",
          }}
        >
          <BrandedImageFallback
            className="skipline-product-media"
            icon={<ShoppingCart fontSize="medium" />}
            label={p.imageSmallUrl ? "" : this.t("common.noImage")}
            brandTheme={this.props.brandTheme}
            sx={{
              position: "absolute",
              inset: 0,
              minHeight: 0,
              transition: "transform 180ms ease, filter 180ms ease",
            }}
          />
          {p.imageSmallUrl ? (
            <Box
              component="img"
              className="skipline-product-media"
              src={p.imageSmallUrl}
              alt={p.name}
              loading="lazy"
              decoding="async"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
              sx={{
                position: "absolute",
                inset: 0,
                m: "auto",
                maxHeight: "88%",
                maxWidth: "88%",
                objectFit: "contain",
                transition: "transform 180ms ease, filter 180ms ease",
              }}
            />
          ) : null}

          {promo ? (
            <Box
              sx={{
                position: "absolute",
                left: 8,
                top: 8,
                bgcolor: "error.main",
                color: "#fff",
                px: 1,
                py: 0.35,
                borderRadius: "8px",
                fontSize: 11,
                fontWeight: 900,
                boxShadow: "0 6px 14px rgba(211, 47, 47, 0.24)",
              }}
            >
              {this.t("skipline.promo")}
            </Box>
          ) : null}

          <IconButton
            size="small"
            aria-label={`${this.t("common.productDetails")} ${p.name}`}
            onClick={() => this.setState({ infoProduct: p })}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              width: 44,
              height: 44,
              borderRadius: "12px",
              bgcolor: "rgba(255,255,255,0.92)",
              color: BRAND,
              border: "1px solid",
              borderColor: BORDER,
              boxShadow: "0 8px 18px rgba(33,20,12,0.12)",
              "&:hover": {
                bgcolor: "#fffaf3",
              },
              ...focusRingSx,
            }}
          >
            <InfoOutlined fontSize="small" />
          </IconButton>

          {qty > 0 && !locked ? (
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{
                position: "absolute",
                right: 8,
                bottom: 8,
                bgcolor: "background.paper",
                px: 0.5,
                py: 0.35,
                borderRadius: "8px",
                boxShadow: "0 10px 22px rgba(33,20,12,0.14)",
                border: "1px solid",
                borderColor: qty > 0 ? "primary.main" : BORDER,
              }}
            >
              <IconButton
                color="error"
                size="small"
                onClick={() => this.decProduct(p.id)}
                aria-label={`${this.t("skipline.decreaseQuantity", "Decrease quantity")} ${p.name}`}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  bgcolor: "rgba(211,47,47,0.08)",
                  ...focusRingSx,
                }}
              >
                <Remove fontSize="small" />
              </IconButton>

              <Typography
                sx={{
                  minWidth: 24,
                  textAlign: "center",
                  fontSize: 14,
                  fontWeight: 900,
                }}
                aria-live="polite"
              >
                {qty}
              </Typography>

              <IconButton
                color="primary"
                size="small"
                onClick={() => this.incProduct(p.id)}
                aria-label={`${this.t("skipline.increaseQuantity", "Increase quantity")} ${p.name}`}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  bgcolor: "rgba(247,181,0,0.12)",
                  ...focusRingSx,
                }}
              >
                <Add fontSize="small" />
              </IconButton>
            </Stack>
          ) : null}
        </Box>

        <CardContent
          sx={{
            flexGrow: 1,
            p: 1.5,
            pb: 1,
          }}
        >
          <Typography
            variant="subtitle2"
            title={p.name}
            sx={{
              fontWeight: 900,
              lineHeight: 1.25,
              minHeight: 38,
              color: BRAND,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {p.name}
          </Typography>

          {price != null ? (
            <Stack
              direction="row"
              spacing={1}
              alignItems="baseline"
              justifyContent="space-between"
              sx={{ mt: 1 }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 900,
                  color: promo ? "error.main" : "text.primary",
                }}
              >
                {money(price, this.state.priceList?.currency || "RON")}
              </Typography>

              {promo && base != null ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    textDecoration: "line-through",
                    fontWeight: 700,
                  }}
                >
                  {money(base, this.state.priceList?.currency || "RON")}
                </Typography>
              ) : null}
            </Stack>
          ) : null}
        </CardContent>

        <CardActions sx={{ p: 1.5, pt: 0 }}>
          <Button
            fullWidth
            variant={qty > 0 ? "outlined" : "contained"}
            size="medium"
            onClick={() => this.addProduct(p)}
            startIcon={<Add />}
            sx={{
              ...pillButtonSx,
              fontWeight: 900,
              py: 0.9,
              minHeight: 46,
              boxShadow: qty > 0 ? "none" : "0 10px 22px rgba(247,181,0,0.16)",
              borderColor: qty > 0 ? "primary.main" : undefined,
            }}
            disabled={locked}
          >
            {this.t("skipline.add")}
          </Button>
        </CardActions>
      </Card>
    );
  };

  renderComboCard = (c)=> {
    const locked = this.state.orderLocked;
    const promo = !!c.isComboPromo;
    return (
      <Card
        variant="outlined"
        sx={{
          ...compactCardSx,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderColor: "secondary.main",
          bgcolor: "rgba(255,253,247,0.98)",
          opacity: locked ? 0.5 : 1,
          boxShadow: "0 16px 38px rgba(15,118,110,0.10)",
          transition: "transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease",
          "&:hover": locked
            ? {}
            : {
                transform: "translateY(-2px)",
                boxShadow: "0 18px 42px rgba(33,20,12,0.12)",
              },
          "&:focus-within": {
            boxShadow: "0 0 0 3px rgba(15,118,110,0.14), 0 16px 36px rgba(33,20,12,0.10)",
          },
          "&:hover .skipline-product-media": locked
            ? {}
            : {
                transform: "scale(1.035)",
                filter: "saturate(1.05)",
              },
        }}
      >
        <Box
          sx={{
            position: "relative",
            height: { xs: 120, sm: 130 },
            bgcolor: "rgba(255,250,243,0.94)",
            borderBottom: "1px solid",
            borderColor: "rgba(124,45,18,0.08)",
            overflow: "hidden",
          }}
        >
          <BrandedImageFallback
            className="skipline-product-media"
            icon={<Tune fontSize="medium" />}
            label={c.imageSmallUrl ? "" : this.t("common.noImage")}
            brandTheme={this.props.brandTheme}
            sx={{
              position: "absolute",
              inset: 0,
              minHeight: 0,
              transition: "transform 180ms ease, filter 180ms ease",
            }}
          />
          {c.imageSmallUrl ? (
            <Box
              component="img"
              className="skipline-product-media"
              src={c.imageSmallUrl}
              alt={c.name}
              loading="lazy"
              decoding="async"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
              sx={{
                position: "absolute",
                inset: 0,
                m: "auto",
                maxHeight: "92%",
                maxWidth: "92%",
                objectFit: "contain",
                transition: "transform 180ms ease, filter 180ms ease",
              }}
            />
          ) : null}
          <Box sx={{ position:'absolute', left:6, top:6, bgcolor:'secondary.main', color:'secondary.contrastText',
                    px:1, py:0.25, borderRadius:"8px", fontSize:12, fontWeight:900 }}>
            {this.t("skipline.combo")}
          </Box>
          {promo && (
            <Box sx={{ position:'absolute', right:6, top:6, bgcolor:'error.main', color:'#fff',
                      px:1, py:0.25, borderRadius:"8px", fontSize:12, fontWeight:900, boxShadow:1 }}>
              {this.t("skipline.promo")}
            </Box>
          )}
        </Box>
        <CardContent sx={{ flexGrow:1, py:1, px:1.25 }}>
          <Typography
            variant="subtitle2"
            title={c.name}
            sx={{
              fontWeight: 900,
              color: BRAND,
              lineHeight: 1.25,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {c.name}
          </Typography>
          <Typography variant="body2" sx={{ mt:0.5, fontWeight: 900 }}>
            {money(fromCents(c.basePriceC), this.state.priceList?.currency||'RON')}+
          </Typography>
        </CardContent>
        <CardActions sx={{ p:1, pt:0 }}>
          <Button fullWidth variant="outlined" size="medium"
                  onClick={()=>this.openComboPicker(c)} startIcon={<Tune />} sx={{ ...pillButtonSx, minHeight: 46, borderColor: "secondary.main" }} disabled={locked}>
            {this.t("skipline.choose")}
          </Button>
        </CardActions>
      </Card>
    );
  };

  // ================================================

  renderNameGate() {
    const { guestEmail, locationInfo, locationOpenNow, locationReason } = this.state;
    const clientOrderActive =
      this.props.authenticatedCheckoutEnabled &&
      this.props.clientPortalAuthenticated;
    const clientPortalBusy =
      this.props.authenticatedCheckoutEnabled &&
      this.isClientPortalBusy();
    const hasOrderEmail =
      this.props.clientProfile?.hasOrderEmail === true;
    const ok =
      !clientPortalBusy &&
      (clientOrderActive || isValidEmail(guestEmail));

    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: PAGE_BG,
        }}
      >
        <AppBar
          position="fixed"
          elevation={0}
          sx={appBarSx}
        >
          <Toolbar sx={{ minHeight: 64 }}>
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="center"
              sx={{ minWidth: 0, flexGrow: 1 }}
            >
              {this.renderBrandMark({ width: 40, height: 40 })}

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 900,
                    letterSpacing: 0.3,
                    lineHeight: 1.1,
                  }}
                  noWrap
                >
                  {this.getBrandNameUpper()}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.25 }}
                  noWrap
                >
                  {locationInfo?.name || this.t("skipline.tagline")}
                </Typography>
              </Box>
            </Stack>

            <Button
              variant="text"
              size="small"
              startIcon={<LocationOnRoundedIcon fontSize="small" />}
              onClick={this.changeLocation}
              aria-label={this.t("skipline.change")}
              sx={{
                ml: 1,
                minWidth: 36,
                px: 0.75,
                fontWeight: 800,
                whiteSpace: "nowrap",
                "& .MuiButton-startIcon": {
                  mr: 0,
                },
              }}
            />

            {this.renderLanguageSwitcher()}
          </Toolbar>
        </AppBar>

        <Container
          maxWidth="sm"
          sx={{
            pt: { xs: 10, sm: 12 },
            pb: { xs: 3, sm: 5 },
          }}
        >
          <Card
            elevation={0}
            sx={{
              ...shellCardSx,
            }}
          >
            <Box
              sx={{
                p: { xs: 2.25, sm: 3 },
                bgcolor: "background.paper",
              }}
            >
              <Box sx={{ textAlign: "center", mb: 2.5 }}>
                <Box
                  sx={{
                    width: 54,
                    height: 54,
                    borderRadius: "8px",
                    bgcolor: ACCENT,
                    color: "#111111",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 1.5,
                    boxShadow: "0 12px 26px rgba(247,181,0,0.22)",
                  }}
                >
                  <ShoppingCart fontSize="medium" />
                </Box>

                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 900,
                    mb: 0.75,
                    letterSpacing: -0.2,
                  }}
                >
                  {this.t("skipline.startOrder")}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    lineHeight: 1.55,
                    maxWidth: 420,
                    mx: "auto",
                  }}
                >
                  {clientPortalBusy
                    ? this.t("skipline.clientSessionRestoring")
                    : clientOrderActive
                      ? this.t("skipline.clientSignedIn")
                      : this.t("skipline.emailNeeded")}
                </Typography>
              </Box>

              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: "8px",
                  bgcolor: locationOpenNow ? "rgba(46, 125, 50, 0.08)" : "rgba(211, 47, 47, 0.08)",
                  border: "1px solid",
                  borderColor: locationOpenNow ? "rgba(46, 125, 50, 0.22)" : "rgba(211, 47, 47, 0.22)",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 900,
                    color: locationOpenNow ? "success.main" : "error.main",
                  }}
                >
                  {locationOpenNow ? this.t("skipline.openNow") : this.t("skipline.closedNow")}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    mt: 0.25,
                    lineHeight: 1.4,
                  }}
                >
                  {locationOpenNow
                    ? this.t("skipline.canContinue")
                    : locationReason || this.t("skipline.onlineUnavailable")}
                </Typography>
              </Box>

              {/* <TextField
                label="Nume"
                fullWidth
                autoFocus
                value={customerName}
                onChange={(e) => this.setState({ customerName: e.target.value })}
                inputProps={{ maxLength: 64 }}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    bgcolor: "#fffaf3",
                  },
                }}
              /> */}

              {clientPortalBusy ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {this.t("skipline.clientSessionRestoring")}
                </Alert>
              ) : clientOrderActive ? (
                <Alert
                  severity={hasOrderEmail ? "success" : "error"}
                  sx={{ mb: 2 }}
                >
                  {this.t(
                    hasOrderEmail
                      ? "skipline.clientAccountEmailReady"
                      : "skipline.clientAccountEmailMissing"
                  )}
                </Alert>
              ) : (
                <TextField
                  label={this.t("skipline.paymentEmail")}
                  type="email"
                  fullWidth
                  value={guestEmail}
                  onChange={(e) => this.setState({ guestEmail: e.target.value })}
                  error={!!guestEmail && !isValidEmail(guestEmail)}
                  helperText={
                    !!guestEmail && !isValidEmail(guestEmail)
                      ? this.t("skipline.invalidEmail")
                      : this.t("skipline.emailHelp")
                  }
                  inputProps={{ maxLength: 128 }}
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      bgcolor: "#fffaf3",
                    },
                  }}
                />
              )}

              <Button
                variant="contained"
                fullWidth
                size="large"
                disabled={!ok || !locationOpenNow}
                onClick={() => this.setState({ view: "home" })}
                sx={{
                  borderRadius: "8px",
                  py: 1.25,
                  fontWeight: 900,
                  boxShadow: ok && locationOpenNow
                    ? "0 12px 26px rgba(247,181,0,0.22)"
                    : "none",
                }}
              >
                {this.t("skipline.continueMenu")}
              </Button>

              {!locationOpenNow ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    mt: 1.5,
                    lineHeight: 1.4,
                  }}
                >
                  {this.t("skipline.menuOnlyOpen")}
                </Typography>
              ) : (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    mt: 1.5,
                    lineHeight: 1.4,
                  }}
                >
                  {clientOrderActive
                    ? this.t("skipline.clientBaseOrderHelp")
                    : this.t("skipline.noAccount")}
                </Typography>
              )}
            </Box>
          </Card>
        </Container>
      </Box>
    );
  }

  renderHome() {
    const tiles = (this.state.categoriesRaw || [])
      .filter(c => c.isForSale !== false)
      .map(c => ({
        id: c.iref || c.id,
        name: c.name,
        image: ensureSmallImage(c.image),
      }));

    const { locationInfo, locationOpenNow, locationReason } = this.state;

    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: PAGE_BG,
        }}
      >
        <AppBar
          position="fixed"
          elevation={0}
          sx={appBarSx}
        >
          <Toolbar sx={{ minHeight: 64 }}>
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="center"
              sx={{ minWidth: 0, flexGrow: 1 }}
            >
              {this.renderBrandMark({ width: 40, height: 40 })}

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 900,
                    letterSpacing: 0.3,
                    lineHeight: 1.1,
                  }}
                  noWrap
                >
                  {this.getBrandNameUpper()}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.25 }}
                  noWrap
                >
                  {locationInfo?.name || this.t("skipline.selectedLocation")}
                </Typography>
              </Box>
            </Stack>

            <Button
              variant="text"
              size="small"
              startIcon={<LocationOnRoundedIcon fontSize="small" />}
              onClick={this.changeLocation}
              aria-label={this.t("skipline.change")}
              sx={{
                ml: 1,
                minWidth: 36,
                px: 0.75,
                fontWeight: 800,
                whiteSpace: "nowrap",
                "& .MuiButton-startIcon": {
                  mr: 0,
                },
              }}
            />

            {this.renderLanguageSwitcher()}
          </Toolbar>
        </AppBar>

        <Container
          maxWidth="lg"
          sx={{
            pt: { xs: 10, sm: 11 },
            pb: { xs: 2, sm: 3 },
          }}
        >
          <Card
            elevation={0}
            sx={{
              ...shellCardSx,
              mb: 2,
            }}
          >
            <Box
              sx={{
                p: { xs: 2.25, sm: 3 },
                bgcolor: "background.paper",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 900,
                      letterSpacing: -0.3,
                      mb: 0.5,
                    }}
                  >
                    {locationInfo?.name || this.getBrandName()}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      lineHeight: 1.55,
                    }}
                  >
                    {this.t("skipline.homeIntro")}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderRadius: "8px",
                    bgcolor: locationOpenNow
                      ? "rgba(46, 125, 50, 0.08)"
                      : "rgba(211, 47, 47, 0.08)",
                    border: "1px solid",
                    borderColor: locationOpenNow
                      ? "rgba(46, 125, 50, 0.22)"
                      : "rgba(211, 47, 47, 0.22)",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 900,
                      color: locationOpenNow ? "success.main" : "error.main",
                    }}
                  >
                    {locationOpenNow ? this.t("skipline.openNow") : this.t("skipline.closedNow")}
                  </Typography>
                </Box>
              </Stack>

              {!locationOpenNow ? (
                <Alert
                  severity="warning"
                  sx={{
                    mt: 2,
                    borderRadius: "8px",
                  }}
                >
                  {locationReason || this.t("skipline.onlineUnavailable")}
                </Alert>
              ) : null}
            </Box>
          </Card>

          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 900,
                letterSpacing: -0.2,
              }}
            >
              {this.t("skipline.chooseCategory")}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {this.t("skipline.chooseCategoryDesc")}
            </Typography>
          </Box>

          {tiles.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: "8px" }}>
              {this.t("skipline.noCategories")}
            </Alert>
          ) : (
            <Grid container spacing={1.5}>
              {tiles.map((t) => (
                <Grid key={t.id} item xs={6} sm={4} md={3} lg={2.4}>
                  <Card
                    onClick={() => locationOpenNow && this.openCategory(t.name)}
                    onKeyDown={(event) =>
                      this.activateCardFromKeyboard(event, () => {
                        if (locationOpenNow) this.openCategory(t.name);
                      })
                    }
                    role="button"
                    tabIndex={locationOpenNow ? 0 : -1}
                    aria-disabled={!locationOpenNow}
                    aria-label={`${t.name}. ${this.t("skipline.viewProducts")}`}
                    elevation={0}
                    sx={{
                      height: "100%",
                      ...compactCardSx,
                      cursor: locationOpenNow ? "pointer" : "default",
                      opacity: locationOpenNow ? 1 : 0.55,
                      transition: "transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease",
                      "&:hover": locationOpenNow
                        ? {
                            transform: "translateY(-2px)",
                            borderColor: "primary.main",
                            boxShadow: "0 16px 36px rgba(33,20,12,0.12)",
                          }
                        : {},
                      "&:focus-visible": {
                        outline: `3px solid ${CTA}`,
                        outlineOffset: 3,
                        borderColor: "secondary.main",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        height: { xs: 118, sm: 132 },
                        bgcolor: "#fff",
                      }}
                    >
                      <BrandedImageFallback
                        icon={<ShoppingCart fontSize="large" />}
                        brandTheme={this.props.brandTheme}
                        sx={{ position: "absolute", inset: 0, minHeight: 0 }}
                      />
                      {t.image ? (
                        <Box
                          component="img"
                          src={t.image}
                          alt={t.name}
                          loading="lazy"
                          decoding="async"
                          onError={(event) => {
                            event.currentTarget.style.display = "none";
                          }}
                          sx={{
                            position: "absolute",
                            inset: 0,
                            m: "auto",
                            maxHeight: "88%",
                            maxWidth: "88%",
                            objectFit: "contain",
                          }}
                        />
                      ) : null}
                    </Box>

                    <CardContent sx={{ p: 1.5 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 900,
                          lineHeight: 1.25,
                          minHeight: 38,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                        title={t.name}
                      >
                        {t.name}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="primary.main"
                        sx={{
                          display: "block",
                          mt: 0.75,
                          fontWeight: 900,
                        }}
                      >
                        {this.t("skipline.viewProducts")}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}

              {this.state.combos?.length > 0 ? (
                <Grid item xs={6} sm={4} md={3} lg={2.4}>
                  <Card
                    onClick={() => this.openCategory("Combos")}
                    onKeyDown={(event) =>
                      this.activateCardFromKeyboard(event, () =>
                        this.openCategory("Combos")
                      )
                    }
                    role="button"
                    tabIndex={0}
                    aria-label={`${this.t("skipline.combo")}. ${this.t("skipline.chooseCombo")}`}
                    elevation={0}
                    sx={{
                      ...compactCardSx,
                      height: "100%",
                      borderColor: "secondary.main",
                      cursor: locationOpenNow ? "pointer" : "default",
                      opacity: locationOpenNow ? 1 : 0.55,
                      transition: "transform 140ms ease, box-shadow 140ms ease",
                      "&:hover": locationOpenNow
                        ? {
                            transform: "translateY(-2px)",
                            boxShadow: "0 16px 36px rgba(33,20,12,0.12)",
                          }
                        : {},
                      "&:focus-visible": {
                        outline: `3px solid ${CTA}`,
                        outlineOffset: 3,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: { xs: 118, sm: 132 },
                        bgcolor: "rgba(109, 76, 65, 0.08)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Tune
                        sx={{
                          fontSize: 48,
                          color: "secondary.main",
                        }}
                      />
                    </Box>

                    <CardContent sx={{ p: 1.5 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 900,
                          lineHeight: 1.25,
                          minHeight: 38,
                        }}
                      >
                        Combos
                      </Typography>

                      <Typography
                        variant="caption"
                        color="secondary.main"
                        sx={{
                          display: "block",
                          mt: 0.75,
                          fontWeight: 900,
                        }}
                      >
                        {this.t("skipline.chooseCombo")}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ) : null}
            </Grid>
          )}
        </Container>
      </Box>
    );
  }

  render(){
    const { loading, error, priceList, products, combos, categories, categoriesRaw, activeCategory, search,
      cartOpen, cartProducts={}, cartCombos={}, placing, view, orderLocked,
      paymentSheetOpen,
      summaryOpen, lastOrderId } = this.state;

    if (!this.state.location) {
      return (
        <ThemeProvider theme={createSkiplineTheme(this.props.brandTheme)}>
          <CssBaseline />
          <GlobalStyles styles={standaloneGlobalStyles} />
          <Box sx={{ ...brandThemeCssVars(this.props.brandTheme), minHeight: "100vh" }}>
            {this.renderLocationSelect()}
          </Box>
        </ThemeProvider>
      );
    }

    const cartQty =
      Object.values(cartProducts).reduce((sum, l) => sum + (l?.qty || 0), 0) +
      Object.values(cartCombos).reduce((sum, l) => sum + (l?.qty || 0), 0);

    const cartIsEmpty = cartQty === 0;
    
    const currency = priceList?.currency || "RON"; const s = (search||"").trim().toLowerCase();
    const currentCategoryId = this.getCategoryIdForName(activeCategory, categoriesRaw);

    const filteredProducts = products
      .filter((p) => {
        if (activeCategory === "Combos") return false;

        const okCat =
          activeCategory === "All" ||
          p.categoryId === currentCategoryId ||
          p.categoryName === activeCategory;

        const okText =
          !s ||
          p.name?.toLowerCase().includes(s) ||
          p.categoryName?.toLowerCase().includes(s);

        return okCat && okText;
      })
      .sort((a, b) => (a.shortName || a.name || "").localeCompare(b.shortName || b.name || ""));

    const filteredCombos = combos
      .filter((c) => {
        const okCat =
          activeCategory === "All" ||
          activeCategory === "Combos" ||
          c.categoryId === currentCategoryId ||
          c.categoryName === activeCategory;

        const okText =
          !s ||
          c.name?.toLowerCase().includes(s) ||
          c.categoryName?.toLowerCase().includes(s);

        return okCat && okText;
      })
      .sort((a, b) => (a.shortName || a.name || "").localeCompare(b.shortName || b.name || ""));

    const itemsCount = Object.values(cartProducts).reduce((a,b)=>a+(b.qty||0),0) + Object.values(cartCombos).reduce((a,b)=>a+(b.qty||0),0);
    const totals = priceList ? this.computeTotals() : { totalGross: 0 };
    const hasLines = Object.keys(cartProducts).length || Object.keys(cartCombos).length;
    const retryPaymentAvailable =
      orderLocked &&
      Boolean(lastOrderId) &&
      this.state.paymentRegistrationFailed;
    const clientOrderActive =
      this.props.authenticatedCheckoutEnabled &&
      this.props.clientPortalAuthenticated;
    const clientAccountEmailMissing =
      clientOrderActive &&
      this.props.clientProfile?.hasOrderEmail !== true;
    const checkoutDisabled =
      (!priceList) ||
      (!hasLines) ||
      placing ||
      (orderLocked && !retryPaymentAvailable) ||
      !this.state.locationOpenNow ||
      (this.props.authenticatedCheckoutEnabled &&
        this.isClientPortalBusy()) ||
      (clientAccountEmailMissing && !retryPaymentAvailable);
    const inFlow = paymentSheetOpen || summaryOpen; 

    return (
      <ThemeProvider theme={createSkiplineTheme(this.props.brandTheme)}>
        <CssBaseline />
        <GlobalStyles styles={standaloneGlobalStyles} />
        <Box sx={{ ...brandThemeCssVars(this.props.brandTheme), flexGrow:1 }}>
          {view==='name' ? this.renderNameGate() : (
            summaryOpen ? (
              <Container
                maxWidth="sm"
                sx={{
                  py: { xs: 1.25, sm: 2 },
                }}
              >
                <OrderSummary
                  id={lastOrderId}
                  currency={currency}
                  cartProducts={cartProducts}
                  cartCombos={cartCombos}
                  gross={this.state.lastOrderGross ?? (priceList ? totals.totalGross : 0)}
                  t={this.t}
                  brandTheme={this.props.brandTheme}
                  onNewOrder={() => {
                    this.pendingCreatedOrder = null;
                    this.setState({
                      cartProducts: {},
                      cartCombos: {},
                      infoProduct: null,
                      orderLocked: false,
                      view: "name",
                      activeCategory: "All",
                      search: "",
                      summaryOpen: false,
                      lastOrderId: null,
                      lastOrderContext: null,
                      lastOrderGross: null,
                      paymentRegistrationFailed: false,
                      paymentRegistering: false,
                      clientSessionRequired: false,
                      customerName: "",
                      guestEmail: "",
                    });
                  }}
                />
              </Container>
            ) : (
              view === 'home' ? this.renderHome() : (
                <Box
                  sx={{
                    minHeight: "100vh",
                    bgcolor: PAGE_BG,
                    pt: 0,
                    pb: { xs: 12, sm: 5 },
                  }}
                >
                  <Box
                    sx={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      right: 0,
                      zIndex: 1090,
                      bgcolor: "rgba(255,250,243,0.94)",
                      borderBottom: "1px solid",
                      borderColor: BORDER,
                      backdropFilter: "blur(18px)",
                      pb: 1,
                    }}
                  >
                    <Container maxWidth="lg" sx={{ pt: 0.75 }}>
                      <Card
                        elevation={0}
                        sx={{
                          ...compactCardSx,
                        }}
                      >
                        <Box sx={{ p: { xs: 1, sm: 1.25 } }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ mb: 1 }}
                          >
                            {this.renderBrandMark({
                              width: { xs: 34, sm: 38 },
                              height: { xs: 34, sm: 38 },
                            })}

                            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 900,
                                  lineHeight: 1.15,
                                }}
                                noWrap
                              >
                                {activeCategory === "All" ? this.t("skipline.menu") : activeCategory}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mt: 0.25 }}
                                noWrap
                              >
                                {this.state.locationInfo?.name || this.getBrandName()}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                px: 1,
                                py: 0.5,
                                borderRadius: "8px",
                                bgcolor: this.state.locationOpenNow
                                  ? "rgba(46, 125, 50, 0.08)"
                                  : "rgba(211, 47, 47, 0.08)",
                                border: "1px solid",
                                borderColor: this.state.locationOpenNow
                                  ? "rgba(46, 125, 50, 0.22)"
                                  : "rgba(211, 47, 47, 0.22)",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 900,
                                  color: this.state.locationOpenNow ? "success.main" : "error.main",
                                }}
                              >
                                {this.state.locationOpenNow ? this.t("skipline.open") : this.t("skipline.closed")}
                              </Typography>
                            </Box>

                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<LocationOnRoundedIcon fontSize="small" />}
                              onClick={this.changeLocation}
                              aria-label={this.t("skipline.change")}
                              sx={{
                                ...pillButtonSx,
                                minWidth: { xs: 40, sm: "auto" },
                                px: { xs: 1, sm: 1.25 },
                                color: BRAND,
                                borderColor: BORDER,
                                bgcolor: "background.paper",
                                whiteSpace: "nowrap",
                                "& .MuiButton-startIcon": {
                                  mr: { xs: 0, sm: 0.5 },
                                },
                                "&:hover": {
                                  borderColor: "secondary.main",
                                  bgcolor: "rgba(15,118,110,0.06)",
                                },
                              }}
                            >
                              <Box
                                component="span"
                                sx={{ display: { xs: "none", sm: "inline" } }}
                              >
                                {this.t("skipline.change")}
                              </Box>
                            </Button>

                            {this.renderLanguageSwitcher()}
                          </Stack>

                          <TextField
                            value={search}
                            onChange={(e) => this.setState({ search: e.target.value })}
                            placeholder={this.t("skipline.searchMenu")}
                            size="small"
                            fullWidth
                            disabled={false}
                            inputProps={{
                              "aria-label": this.t("skipline.searchMenu"),
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Search fontSize="small" />
                                </InputAdornment>
                              ),
                              endAdornment: search ? (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    aria-label={this.t("skipline.clearFilter")}
                                    onClick={() => this.setState({ search: "" })}
                                  >
                                    <Close fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ) : null,
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                bgcolor: "#fffaf3",
                              },
                            }}
                          />

                          <Tabs
                            value={Math.max(0, categories.indexOf(activeCategory))}
                            onChange={(e, idx) =>
                              this.setState({ activeCategory: categories[idx] || "All" })
                            }
                            variant="scrollable"
                            scrollButtons
                            allowScrollButtonsMobile
                            sx={{
                              mt: 1,
                              minHeight: 40,
                              "& .MuiTabs-flexContainer": {
                                gap: 0.5,
                              },
                              "& .MuiTab-root": {
                                textTransform: "none",
                                fontWeight: 900,
                                minHeight: 38,
                                px: 1.5,
                                borderRadius: "8px",
                                color: "text.primary",
                                border: "1px solid",
                                borderColor: BORDER,
                                bgcolor: "background.paper",
                              },
                              "& .MuiTab-root.Mui-selected": {
                                bgcolor: "secondary.main",
                                color: "#ffffff !important",
                                borderColor: "secondary.main",
                              },
                              "& .MuiTabs-indicator": {
                                display: "none",
                              },
                            }}
                          >
                            {categories.map((c) => (
                              <Tab key={c} label={c === "All" ? this.t("skipline.allMenu") : c} />
                            ))}
                          </Tabs>

                          {!this.state.locationOpenNow ? (
                            <Alert
                              severity="warning"
                              sx={{
                                mt: 1,
                                borderRadius: "8px",
                              }}
                            >
                              {this.state.locationReason || this.t("skipline.onlineUnavailable")}
                            </Alert>
                          ) : null}
                        </Box>
                      </Card>
                    </Container>
                  </Box>

                  <Container maxWidth="lg" sx={{ pt: { xs: 18, sm: 17 } }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 1.5 }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 900,
                            letterSpacing: -0.2,
                          }}
                          noWrap
                        >
                          {activeCategory === "All" ? this.t("skipline.allProducts") : activeCategory}
                        </Typography>

                        <Typography variant="caption" color="text.secondary">
                          {this.tr("skipline.availableProducts", {
                            count: filteredProducts.length + filteredCombos.length,
                          })}
                        </Typography>
                      </Box>
                    </Stack>

                    {loading ? (
                      <Grid container spacing={1.5}>
                        {Array.from({ length: 10 }).map((_, i) => (
                          <Grid item xs={6} sm={4} md={3} lg={2.4} key={i}>
                            <Card
                              elevation={0}
                              sx={{
                                ...compactCardSx,
                                border: "1px solid",
                                overflow: "hidden",
                              }}
                            >
                              <CardContent sx={{ p: 1.5 }}>
                                <Skeleton
                                  variant="rounded"
                                  height={132}
                                  sx={{
                                    borderRadius: "8px",
                                    bgcolor: "rgba(254,215,170,0.34)",
                                  }}
                                />
                                <Skeleton width="80%" sx={{ mt: 1 }} />
                                <Skeleton width="45%" />
                              </CardContent>
                              <CardActions sx={{ p: 1.5, pt: 0 }}>
                                <Skeleton
                                  variant="rounded"
                                  width="100%"
                                  height={38}
                                  sx={{ borderRadius: "8px" }}
                                />
                              </CardActions>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <>
                        <Grid container spacing={1.5}>
                          {filteredProducts.map((p) => (
                            <Grid item xs={6} sm={4} md={3} lg={2.4} key={p.id}>
                              {this.renderProductCard(p)}
                            </Grid>
                          ))}

                          {filteredCombos.map((c) => (
                            <Grid item xs={6} sm={4} md={3} lg={2.4} key={c.id}>
                              {this.renderComboCard(c)}
                            </Grid>
                          ))}

                          {!filteredProducts.length && !filteredCombos.length ? (
                            <Grid item xs={12}>
                              <Alert
                                severity="info"
                                sx={{
                                  borderRadius: "8px",
                                }}
                              >
                                {this.t("skipline.noProductsCategory")}
                              </Alert>
                            </Grid>
                          ) : null}
                        </Grid>
                      </>
                    )}
                  </Container>
                </Box>
              )
            )
          )}

          {view !== 'name' && !summaryOpen && !inFlow && (
            <Box
              component="aside"
              aria-label={this.t("skipline.cart")}
              sx={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 1200,
                bgcolor: "rgba(255,255,255,0.96)",
                backdropFilter: "blur(18px)",
                borderTop: "1px solid",
                borderColor: BORDER,
                pt: 1,
                pb: "max(8px, env(safe-area-inset-bottom))",
                boxShadow: "0 -12px 34px rgba(33,20,12,0.12)",
              }}
            >
              <Container
                maxWidth="lg"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                }}
              >
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      fontWeight: 700,
                      lineHeight: 1.1,
                    }}
                  >
                    {this.t("skipline.totalOrder")}
                  </Typography>

                  <Typography
                    variant="h6"
                    aria-live="polite"
                    sx={{
                      fontWeight: 900,
                      lineHeight: 1.2,
                    }}
                    noWrap
                  >
                    {money(totals.totalGross || 0, currency)}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={
                    <Badge
                      badgeContent={itemsCount}
                      color="secondary"
                      sx={{
                        "& .MuiBadge-badge": {
                          fontWeight: 900,
                        },
                      }}
                    >
                      <ShoppingCart />
                    </Badge>
                  }
                  onClick={() => !inFlow && this.setState({ cartOpen: true })}
                  aria-label={`${this.t("skipline.viewCart")}: ${itemsCount}, ${money(totals.totalGross || 0, currency)}`}
                  sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.15,
                    ...pillButtonSx,
                    fontWeight: 900,
                    whiteSpace: "nowrap",
                    boxShadow: "0 10px 24px rgba(247, 181, 0, 0.25)",
                  }}
                >
                  {itemsCount > 0 ? this.t("skipline.viewCart") : this.t("skipline.cart")}
                </Button>
              </Container>
            </Box>
          )}

          {!inFlow && (
            <Drawer
              anchor="right"
              open={cartOpen && view !== 'name'}
              onClose={() => this.setState({ cartOpen: false })}
              PaperProps={{
                sx: {
                  ...brandThemeCssVars(this.props.brandTheme),
                  width: { xs: "100%", sm: 440 },
                  maxWidth: "100vw",
                  bgcolor: PAGE_BG,
                },
                role: "dialog",
                "aria-modal": true,
                "aria-labelledby": "skipline-cart-title",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  bgcolor: PAGE_BG,
                }}
              >
                <Box
                  component="header"
                  sx={{
                    p: 2,
                    bgcolor: SURFACE,
                    borderBottom: "1px solid",
                    borderColor: BORDER,
                    boxShadow: "0 10px 30px rgba(33,20,12,0.06)",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "8px",
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 8px 20px rgba(247, 181, 0, 0.22)",
                        flexShrink: 0,
                      }}
                    >
                      <ShoppingCart fontSize="small" />
                    </Box>

                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography
                        id="skipline-cart-title"
                        variant="h6"
                        sx={{
                          fontWeight: 900,
                          lineHeight: 1.15,
                        }}
                      >
                        {this.t("skipline.yourOrder")}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        aria-live="polite"
                        sx={{ display: "block", mt: 0.25 }}
                      >
                        {itemsCount} {itemsCount === 1 ? this.t("skipline.itemOne") : this.t("skipline.itemMany")} {this.t("skipline.inCart")}
                      </Typography>
                    </Box>

                    <IconButton
                      size="small"
                      onClick={() => this.setState({ cartOpen: false })}
                      aria-label={this.t("skipline.closeCart")}
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: PAGE_BG,
                        borderRadius: "12px",
                        border: "1px solid",
                        borderColor: BORDER,
                        "&:hover": {
                          bgcolor: "#fff3cc",
                        },
                        ...focusRingSx,
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
                <Box
                  component="section"
                  role="region"
                  aria-label={this.t("skipline.yourOrder")}
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overscrollBehavior: "contain",
                    p: 1.25,
                  }}
                >
                  <List dense sx={{ p: 0 }}>
                    {Object.values(cartProducts).map((it) => {
                      const unit = fromCents(it.unitPriceC);
                      const lineTotal = unit * it.qty;

                      return (
                        <ListItem
                          key={it.id}
                          disableGutters
                          sx={{
                            ...compactCardSx,
                            mb: 1,
                            p: 1.25,
                            bgcolor: "background.paper",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              width: "100%",
                              minWidth: 0,
                              gap: 1.25,
                            }}
                          >
                            <Box
                              sx={{
                                position: "relative",
                                width: 58,
                                height: 58,
                                flexShrink: 0,
                              }}
                            >
                              <Avatar
                                variant="rounded"
                                src={it.imageSmallUrl || undefined}
                                sx={{
                                  width: 58,
                                  height: 58,
                                  borderRadius: "8px",
                                  bgcolor: "#fffaf3",
                                  border: "1px solid",
                                  borderColor: BORDER,
                                }}
                              >
                                {it.name?.[0] || "?"}
                              </Avatar>

                              {it.qty > 1 ? (
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: -7,
                                    right: -7,
                                    bgcolor: "primary.main",
                                    color: "primary.contrastText",
                                    fontSize: 12,
                                    fontWeight: 900,
                                    lineHeight: 1,
                                    px: 0.7,
                                    py: 0.45,
                                    borderRadius: "8px",
                                    boxShadow: "0 5px 12px rgba(247, 181, 0, 0.25)",
                                    minWidth: 22,
                                    textAlign: "center",
                                  }}
                                >
                                  ×{it.qty}
                                </Box>
                              ) : null}
                            </Box>

                            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 900,
                                  lineHeight: 1.25,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                                title={it.name}
                              >
                                {it.name}
                              </Typography>

                              <Stack
                                direction="row"
                                spacing={0.75}
                                alignItems="baseline"
                                sx={{ mt: 0.5 }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 900,
                                  }}
                                >
                                  {money(lineTotal, currency)}
                                </Typography>

                                {it.qty > 1 ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    {money(unit, currency)} / {this.t("skipline.piece")}
                                  </Typography>
                                ) : null}
                              </Stack>
                            </Box>

                            <Box sx={{ flexShrink: 0 }}>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                                sx={{
                                  bgcolor: "#fffaf3",
                                  border: "1px solid",
                                  borderColor: BORDER,
                                  borderRadius: "8px",
                                  px: 0.4,
                                  py: 0.3,
                                }}
                              >
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => this.decProduct(it.id)}
                                  disabled={orderLocked}
                                  aria-label={`${this.t("skipline.decreaseQuantity", "Decrease quantity")} ${it.name}`}
                                  sx={{
                                    width: 44,
                                    height: 44,
                                    ...focusRingSx,
                                  }}
                                >
                                  <Remove fontSize="small" />
                                </IconButton>

                                <Typography
                                  variant="body2"
                                  sx={{
                                    minWidth: 26,
                                    textAlign: "center",
                                    fontWeight: 900,
                                  }}
                                >
                                  {it.qty}
                                </Typography>

                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => this.incProduct(it.id)}
                                  disabled={orderLocked}
                                  aria-label={`${this.t("skipline.increaseQuantity", "Increase quantity")} ${it.name}`}
                                  sx={{
                                    width: 44,
                                    height: 44,
                                    ...focusRingSx,
                                  }}
                                >
                                  <Add fontSize="small" />
                                </IconButton>
                              </Stack>
                            </Box>
                          </Box>
                        </ListItem>
                      );
                    })}

                    {Object.entries(cartCombos).map(([key, it]) => {
                      const qty = it.qty || 1;

                      // Gather all items (selected + fixed)
                      const all = [
                        ...(it.selectionDetails || []),
                        ...(it.fixedItems || []),
                      ];

                      const type1 = all.filter(x => x.priceType === 1);
                      const type2 = all.filter(x => x.priceType === 2);
                      const type0 = all.filter(x => x.priceType === 0);

                      // Per‑unit parts
                      const sum1UnitRON = type1.reduce((acc, s) => acc + (this.state.effectiveProd?.[s.productId] || 0), 0);
                      const sum2UnitRON = type2.reduce((acc, s) => acc + ((s.priceValueC || 0) / 100), 0);
                      const baseUnitRON = (it.basePriceC || 0) / 100;

                      // Allocate base → type0 (RON per unit), proportional to pricelist P0 and fix ±0.01 to the largest P0
                      const P0 = type0.map(s => this.anchorForType0(s.productId));
                      const S0 = P0.reduce((a, b) => a + b, 0);
                      let alloc0UnitRON = [];
                      if (type0.length) {
                        if (S0 <= 0) {
                          const each = Math.round((baseUnitRON / type0.length) * 100) / 100;
                          alloc0UnitRON = Array(type0.length).fill(each);
                          const drift = Math.round((baseUnitRON - alloc0UnitRON.reduce((a,b)=>Math.round((a+b)*100)/100,0)) * 100)/100;
                          if (Math.abs(drift) >= 0.01) alloc0UnitRON[0] = Math.round((alloc0UnitRON[0] + drift) * 100)/100;
                        } else {
                          alloc0UnitRON = P0.map(p => Math.round((baseUnitRON * (p / S0)) * 100) / 100);
                          let sum = Math.round(alloc0UnitRON.reduce((a,b)=>Math.round((a+b)*100)/100,0) * 100)/100;
                          let rem = Math.round((baseUnitRON - sum) * 100)/100;
                          if (Math.abs(rem) >= 0.01) {
                            const idx = P0.indexOf(Math.max(...P0));
                            alloc0UnitRON[idx] = Math.round((alloc0UnitRON[idx] + rem) * 100)/100;
                          }
                        }
                      }

                      // Build breakdown rows (per line, after qty)
                      const rows = [];
                      type1.forEach((s, i) => {
                        const vUnit = this.state.effectiveProd?.[s.productId] || 0;
                        rows.push({ name: s.name, value: Math.round(vUnit*qty*100)/100 });
                      });
                      type2.forEach((s, i) => {
                        const vUnit = (s.priceValueC || 0) / 100;
                        rows.push({ name: s.name, value: Math.round(vUnit*qty*100)/100 });
                      });
                      type0.forEach((s, i) => {
                        rows.push({ name: s.name, value: Math.round(alloc0UnitRON[i]*qty*100)/100 });
                      });

                      // Final line total (must equal Σ rows)
                      const unitLineRON = baseUnitRON + sum1UnitRON + sum2UnitRON;
                      const lineTotal = Math.round(unitLineRON * qty * 100) / 100;

                      return (
                        <ListItem
                          key={key}
                          disableGutters
                          sx={{
                            ...compactCardSx,
                            mb: 1,
                            p: 1.25,
                            bgcolor: "background.paper",
                            borderColor: "secondary.main",
                            boxShadow: "0 10px 26px rgba(15, 118, 110, 0.10)",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              width: "100%",
                              minWidth: 0,
                              gap: 1.25,
                            }}
                          >
                            <Box
                              sx={{
                                position: "relative",
                                width: 58,
                                height: 58,
                                flexShrink: 0,
                              }}
                            >
                              <Avatar
                                variant="rounded"
                                src={it.imageSmallUrl || undefined}
                                sx={{
                                  width: 58,
                                  height: 58,
                                  borderRadius: "8px",
                                  bgcolor: "rgba(109, 76, 65, 0.08)",
                                  border: "1px solid",
                                  borderColor: BORDER,
                                }}
                              >
                                🧩
                              </Avatar>

                              <Box
                                sx={{
                                  position: "absolute",
                                  bottom: -5,
                                  left: 2,
                                  bgcolor: "secondary.main",
                                  color: "secondary.contrastText",
                                  fontSize: 10,
                                  fontWeight: 900,
                                  px: 0.65,
                                  py: 0.25,
                                  borderRadius: "8px",
                                  boxShadow: "0 5px 12px rgba(109, 76, 65, 0.22)",
                                }}
                              >
                                {this.t("skipline.combo")}
                              </Box>

                              {it.qty > 1 ? (
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: -7,
                                    right: -7,
                                    bgcolor: "primary.main",
                                    color: "primary.contrastText",
                                    fontSize: 12,
                                    fontWeight: 900,
                                    lineHeight: 1,
                                    px: 0.7,
                                    py: 0.45,
                                    borderRadius: "8px",
                                    boxShadow: "0 5px 12px rgba(247, 181, 0, 0.25)",
                                    minWidth: 22,
                                    textAlign: "center",
                                  }}
                                >
                                  ×{it.qty}
                                </Box>
                              ) : null}
                            </Box>

                            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                              <Typography
                                variant="subtitle2"
                                title={it.name}
                                sx={{
                                  fontWeight: 900,
                                  lineHeight: 1.25,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {it.name}
                              </Typography>

                              <Stack
                                direction="row"
                                spacing={0.75}
                                alignItems="baseline"
                                sx={{ mt: 0.5 }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 900,
                                  }}
                                >
                                  {money(lineTotal, this.state.priceList?.currency || "RON")}
                                </Typography>

                                {qty > 1 ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    {money(lineTotal / qty, this.state.priceList?.currency || "RON")} / combo
                                  </Typography>
                                ) : null}
                              </Stack>

                              {rows.length > 0 ? (
                                <Box
                                  sx={{
                                    mt: 0.75,
                                    p: 1,
                                    borderRadius: "8px",
                                    bgcolor: "#fffaf3",
                                    border: "1px solid",
                                    borderColor: BORDER,
                                  }}
                                >
                                  {rows.map((r, idx) => (
                                    <Box
                                      key={idx}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        py: 0.15,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        title={r.name}
                                        sx={{
                                          minWidth: 0,
                                          pr: 1,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {r.name}
                                      </Typography>

                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontWeight: 800,
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {money(r.value, this.state.priceList?.currency || "RON")}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              ) : null}
                            </Box>

                            <Box sx={{ flexShrink: 0 }}>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                                sx={{
                                  bgcolor: "#fffaf3",
                                  border: "1px solid",
                                  borderColor: BORDER,
                                  borderRadius: "8px",
                                  px: 0.4,
                                  py: 0.3,
                                }}
                              >
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => this.decCombo(key)}
                                  disabled={orderLocked}
                                  aria-label={`${this.t("skipline.decreaseQuantity", "Decrease quantity")} ${it.name}`}
                                  sx={{
                                    width: 44,
                                    height: 44,
                                    ...focusRingSx,
                                  }}
                                >
                                  <Remove fontSize="small" />
                                </IconButton>

                                <Typography
                                  variant="body2"
                                  sx={{
                                    minWidth: 26,
                                    textAlign: "center",
                                    fontWeight: 900,
                                  }}
                                >
                                  {it.qty}
                                </Typography>

                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => this.incCombo(key)}
                                  disabled={orderLocked}
                                  aria-label={`${this.t("skipline.increaseQuantity", "Increase quantity")} ${it.name}`}
                                  sx={{
                                    width: 44,
                                    height: 44,
                                    ...focusRingSx,
                                  }}
                                >
                                  <Add fontSize="small" />
                                </IconButton>
                              </Stack>
                            </Box>
                          </Box>
                        </ListItem>
                      );
                    })}

                    {cartIsEmpty && (
                      <Box
                        sx={{
                          ...compactCardSx,
                          p: 3,
                          textAlign: "center",
                          bgcolor: "background.paper",
                        }}
                      >
                        <Box
                          sx={{
                            width: 58,
                            height: 58,
                            borderRadius: "8px",
                            bgcolor: "rgba(247, 181, 0, 0.12)",
                            color: "primary.main",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mx: "auto",
                            mb: 1.5,
                          }}
                        >
                          <ShoppingCart fontSize="medium" />
                        </Box>

                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 900,
                            mb: 0.5,
                          }}
                        >
                          {this.t("skipline.emptyCart")}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            lineHeight: 1.5,
                            mb: 2,
                          }}
                        >
                          {this.t("skipline.emptyCartDesc")}
                        </Typography>

                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() => this.setState({ cartOpen: false })}
                          sx={{
                            ...pillButtonSx,
                            fontWeight: 900,
                            py: 1,
                          }}
                        >
                          {this.t("skipline.backToMenu")}
                        </Button>
                      </Box>
                    )}
                  </List>
                </Box>

                <Box
                  component="footer"
                  sx={{
                    p: 2,
                    pb: "max(16px, env(safe-area-inset-bottom))",
                    bgcolor: SURFACE,
                    borderTop: "1px solid",
                    borderColor: BORDER,
                    boxShadow: "0 -10px 28px rgba(33,20,12,0.10)",
                    flexShrink: 0,
                    maxHeight: "62dvh",
                    overflowY: "auto",
                    overscrollBehavior: "contain",
                  }}
                >
                  <Stack spacing={1.25}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: "8px",
                        bgcolor: "#fffaf3",
                        border: "1px solid",
                        borderColor: BORDER,
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontWeight: 700 }}
                        >
                          {this.t("skipline.totalOrder")}
                        </Typography>

                        <Typography
                          variant="h6"
                          aria-live="polite"
                          sx={{
                            fontWeight: 900,
                            lineHeight: 1.15,
                          }}
                        >
                          {money(totals.totalGross || 0, currency)}
                        </Typography>
                      </Stack>

                      {!this.state.locationOpenNow ? (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{
                            display: "block",
                            mt: 0.75,
                            lineHeight: 1.35,
                          }}
                        >
                          {this.state.locationReason || this.t("skipline.locationUnavailable")}
                        </Typography>
                      ) : null}
                    </Box>

                    <ClientCheckoutAuthPanel
                      enabled={
                        this.props.clientPortalEnabled &&
                        this.props.authenticatedCheckoutEnabled
                      }
                      authenticated={this.props.clientPortalAuthenticated}
                      displayName={this.props.clientProfile?.displayName}
                      locked={
                        orderLocked ||
                        placing ||
                        this.isClientPortalBusy()
                      }
                      onLogin={this.props.loginClient}
                      onLogout={this.logoutClientBeforeOrder}
                      onRefresh={this.props.refreshAfterOrderOutcome}
                      t={this.t}
                      brandTheme={this.props.brandTheme}
                    />

                    {this.props.authenticatedCheckoutEnabled &&
                    this.props.clientPortalAuthenticated ? (
                      <Alert
                        severity={
                          this.props.clientProfile?.hasOrderEmail === true
                            ? "info"
                            : "error"
                        }
                        variant="outlined"
                      >
                        <Typography variant="body2" fontWeight={900}>
                          {this.t("skipline.clientCheckoutActive")}
                        </Typography>
                        <Typography variant="caption" component="p">
                          {this.t(
                            this.props.clientProfile?.hasOrderEmail === true
                              ? "skipline.clientBaseOrderHelp"
                              : "skipline.clientAccountEmailMissing"
                          )}
                        </Typography>
                      </Alert>
                    ) : null}

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={this.clearCart}
                        disabled={!hasLines || orderLocked}
                        sx={{
                          ...pillButtonSx,
                          fontWeight: 900,
                          py: 1.1,
                          minHeight: 48,
                        }}
                      >
                        {this.t("skipline.clearCart")}
                      </Button>

                      <Button
                        variant="contained"
                        endIcon={!placing ? <Send /> : null}
                        fullWidth
                        onClick={
                          retryPaymentAvailable
                            ? this.retryPaymentRegistration
                            : this.checkout
                        }
                        disabled={checkoutDisabled}
                        aria-busy={placing}
                        sx={{
                          ...pillButtonSx,
                          fontWeight: 900,
                          py: 1.1,
                          minHeight: 50,
                          boxShadow: !checkoutDisabled
                            ? "0 10px 24px rgba(247, 181, 0, 0.25)"
                            : "none",
                        }}
                      >
                        {placing ? (
                          <CircularProgress size={20} sx={{ color: "white" }} />
                        ) : (
                          retryPaymentAvailable
                            ? this.t("skipline.retryPaymentRegistration")
                            : this.t("skipline.checkout")
                        )}
                      </Button>
                    </Stack>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        textAlign: "center",
                        lineHeight: 1.35,
                      }}
                    >
                      {this.tr("skipline.securePayment", { provider: this.state.paymentProvider })}
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Drawer>
          )}

          <ComboPickerSheet
            open={this.state.pickerOpen}
            combo={this.state.pickerCombo}
            productsPool={this.state.productsAll || this.state.products}
            priceList={priceList}
            promoByProductId={this.state.promoByProductId}   // <— pass it down
            promoByComboId={this.state.promoByComboId}   // <— pass it down
            onClose={()=>this.setState({ pickerOpen:false, pickerCombo:null })}
            onConfirm={this.confirmComboFromPicker}
            t={this.t}
            brandTheme={this.props.brandTheme}
          />
          <ProductInfoModal
            open={Boolean(this.state.infoProduct)}
            onClose={() => this.setState({ infoProduct: null })}
            product={this.state.infoProduct}
            categoryName={this.state.infoProduct?.categoryName || ""}
            brandTheme={this.props.brandTheme}
            allergens={this.props.getProductAllergens?.(this.state.infoProduct) || []}
            t={this.t}
          />
          <PaymentSheet
            open={this.state.paymentSheetOpen}
            payUrl={this.state.payUrl}
            message={this.state.paymentMessage}
            checking={this.state.paymentChecking}
            iframeError={this.state.payIframeError}
            paymentProvider={this.state.paymentProvider}
            brandName={this.getBrandName()}
            registrationFailed={this.state.paymentRegistrationFailed}
            registering={this.state.paymentRegistering}
            clientSessionRequired={this.state.clientSessionRequired}
            onOpenPayU={this.openPayU}
            onManualCheck={this.checkOrderStatus}
            onCancelOrder={this.cancelCurrentOrder}
            onRetryRegistration={this.retryPaymentRegistration}
            onClientLogin={this.restoreClientSession}
            onClose={
              !this.state.lastOrderId
                ? () =>
                    this.setState({
                      paymentSheetOpen: false,
                      clientSessionRequired: false,
                    })
                : undefined
            }
            t={this.t}
            brandTheme={this.props.brandTheme}
          />

          <Snackbar open={!!error} autoHideDuration={6000} onClose={()=>this.setState({ error:"" })}><Alert onClose={()=>this.setState({ error:"" })} severity="error" variant="filled">{error}</Alert></Snackbar>
        </Box>
      </ThemeProvider>
    );
  }
}

export default function SkipTheLinePageWithStore(props) {
  const {
    logoUrl,
    bannerUrl,
    brandName,
    brandTheme,
    publicConfigReady,
    publicImagesReady,
    languages,
    selectedLanguageIref,
    selectedLanguage,
    defaultLanguage,
    languageCode,
    setSelectedLanguageIref,
    getProductAllergens,
    t,
  } = useDataStore();
  const clientPortal = useClientPortal();

  return (
    <SkipTheLinePage
      {...props}
      logoUrl={logoUrl}
      bannerUrl={bannerUrl}
      brandName={brandName}
      brandTheme={brandTheme}
      publicConfigReady={publicConfigReady}
      publicImagesReady={publicImagesReady}
      languages={languages}
      selectedLanguageIref={selectedLanguageIref}
      selectedLanguage={selectedLanguage}
      defaultLanguage={defaultLanguage}
      languageCode={languageCode}
      setSelectedLanguageIref={setSelectedLanguageIref}
      getProductAllergens={getProductAllergens}
      t={t}
      clientPortalAuthenticated={clientPortal.isAuthenticated}
      clientPortalEnabled={clientPortal.enabled}
      clientPortalRestoring={clientPortal.isRestoring}
      clientPortalBusy={
        clientPortal.status === "restoring" ||
        clientPortal.status === "authenticating"
      }
      authenticatedCheckoutEnabled={
        clientPortal.authenticatedCheckoutEnabled
      }
      clientProfile={clientPortal.profile}
      addAuthenticatedOrder={clientPortal.addAuthenticatedOrder}
      registerAuthenticatedPay={clientPortal.registerAuthenticatedPay}
      getAuthenticatedPayStatus={clientPortal.getAuthenticatedPayStatus}
      cancelAuthenticatedOrder={clientPortal.cancelAuthenticatedOrder}
      refreshAfterOrderOutcome={clientPortal.refreshAfterOrderOutcome}
      loginClient={clientPortal.login}
      logoutClient={clientPortal.logout}
    />
  );
}
